import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'nli-site-language';

export const LANGUAGE_OPTIONS = [
  { code: 'en', dir: 'ltr' },
  { code: 'ar', dir: 'rtl' },
  { code: 'fr', dir: 'ltr' },
];

const TRANSLATIONS = {
  en: {
    'language.label': 'Language',
    'language.en': 'English',
    'language.ar': 'Arabic',
    'language.fr': 'French',
    'header.home': 'Home',
    'header.courses': 'Courses',
    'header.universityProgression': 'University Progression',
    'header.applyNow': 'Apply Now',
    'header.aboutUs': 'About Us',
    'header.howItWorks': 'How It Works',
    'header.studentSupport': 'Student Support',
    'header.feesPricing': 'Fees & Pricing',
    'header.contact': 'Contact',
    'header.studentPortal': 'Student Portal',
    'header.allCourses': 'All Courses',
    'header.awardingBodies': 'Awarding Bodies',
    'header.othmQualifications': 'OTHM Qualifications',
    'header.qualifiQualifications': 'QUALIFI Qualifications',
    'header.toggleMenu': 'Toggle menu',
    'footer.tagline': 'Empowering learners across the UK with flexible, accredited distance learning programmes. Study at your own pace, achieve your career goals.',
    'footer.courseCategories': 'Course Categories',
    'footer.quickLinks': 'Quick Links',
    'footer.contactUs': 'Contact Us',
    'footer.accreditation': 'Awarding bodies available through Atlas Academy: OTHM & QUALIFI.',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.termsConditions': 'Terms & Conditions',
    'footer.cookiePolicy': 'Cookie Policy',
    'footer.termsOfUse': 'Terms of Use',
    'footer.rightsReserved': '© {year} Atlas Academy Ltd. All rights reserved.',
    'course.backToCourses': 'Back to Courses',
    'course.notFound': 'Course not found.',
    'course.students': 'students',
    'course.reviews': 'reviews',
    'course.aboutTitle': 'About This Course',
    'course.aboutSupplement': 'This qualification is fully delivered online through our secure student portal. You will have access to all study materials, assessments, and tutor feedback from day one of enrolment. There are no fixed timetables — you simply study around your life.',
    'course.studyTitle': 'What You Will Study',
    'course.detailsTitle': 'Course Details',
    'course.detail.level': 'Level',
    'course.detail.duration': 'Duration',
    'course.detail.studyMode': 'Study Mode',
    'course.detail.studyModeValue': 'Online / Distance Learning',
    'course.detail.awardingBody': 'Awarding Body',
    'course.detail.assessment': 'Assessment',
    'course.detail.assessmentValue': 'Portfolio / Written Assignments',
    'course.detail.certificate': 'Certificate',
    'course.detail.certificateValue': 'Official UK Qualification',
    'course.paymentOptions': 'Payment Options',
    'course.toComplete': 'to complete',
    'course.modulesCount': 'course modules',
    'course.accreditedBy': 'Accredited by',
    'course.flexiblePayment': 'Flexible payment plan available',
    'course.enrolNow': 'Enrol Now',
    'course.requestCallback': 'Request a Callback',
    'course.paySecurely': 'Pay Securely',
    'course.paymentHelp': 'Secure checkout powered by Stripe.',
    'course.paymentSuccess': 'Payment completed successfully. Your enrolment team will verify the next steps shortly.',
    'course.paymentCancelled': 'Payment was cancelled. You can resume checkout whenever you are ready.',
    'course.paymentError': 'Unable to start secure payment right now. Please try again shortly.',
    'course.enquirySubmitted': 'Enquiry Submitted!',
    'course.callbackSuccess': 'Our team will contact you within 24 hours to complete your enrolment.',
    'course.callbackTitle': 'Request a Callback',
    'course.firstName': 'First name',
    'course.lastName': 'Last name',
    'course.emailAddress': 'Email address',
    'course.phoneNumber': 'Phone number',
    'course.sending': 'Sending…',
    'course.submitEnquiry': 'Submit Enquiry',
    'course.cancel': 'Cancel',
    'course.relatedCourses': 'Related Courses',
    'course.youSave': 'You save £{amount}',
    'apply.heroEyebrow': 'Online application form',
    'apply.heroTitle': 'Apply for Your Course',
    'apply.heroDescription': 'Information requested on this form should be completed in as much detail in order to process your application successfully. All fields marked * must be completed.',
    'apply.applicationReceived': 'Application received',
    'apply.successIntro': 'Thank you, {firstName}. Your application for {courseTitle} has been submitted successfully.',
    'apply.successFollowup': 'A member of our admissions team will contact you within one working day to verify your details and complete your enrolment. A confirmation email has been sent to {email}.',
    'apply.browseMoreCourses': 'Browse more courses',
    'apply.returnHome': 'Return to home',
    'apply.courseSelection': 'Course Selection',
    'apply.selectCourse': 'Select course',
    'apply.loadingCourses': '-- Loading courses --',
    'apply.selectCoursePlaceholder': '-- Select a course --',
    'apply.currentCourseFee': 'Current course fee: £{amount}',
    'apply.studyMode': 'Study Mode',
    'apply.studyMode.online': 'Online',
    'apply.studyMode.blended': 'Blended learning',
    'apply.feesMode': 'Course Fees (Mode)',
    'apply.feesMode.selfFunded': 'Self Funded',
    'apply.feesMode.company': 'Company Sponsorship',
    'apply.feesMode.loan': 'Bank Loan',
    'apply.paymentMethod': 'Payment Method',
    'apply.paymentMethod.full': 'Pay in full (one time payment)',
    'apply.paymentMethod.installments': 'Monthly installments',
    'apply.courseStartingDate': 'Course Starting Date',
    'apply.previouslyStudied': 'Have you previously studied at Atlas Academy?',
    'apply.heardAbout': 'Where did you first hear about us?',
    'apply.hear.google': 'Google',
    'apply.hear.bing': 'Bing',
    'apply.hear.social': 'Social Media',
    'apply.hear.referral': 'Friend / Family / Referral',
    'apply.hear.other': 'Other (Please Specify)',
    'apply.selectOption': 'Select Option',
    'apply.other': 'Other',
    'apply.yes': 'Yes',
    'apply.no': 'No',
    'apply.pleaseSpecify': 'Please specify',
    'apply.promoCode': 'Promo Code / Referral Code (optional)',
    'apply.personalInformation': 'Personal Information',
    'apply.title': 'Title',
    'apply.title.mr': 'Mr.',
    'apply.title.mrs': 'Mrs.',
    'apply.title.miss': 'Miss.',
    'apply.title.ms': 'Ms.',
    'apply.title.dr': 'Dr.',
    'apply.firstName': 'First Name',
    'apply.middleName': 'Middle Name',
    'apply.surname': 'Surname',
    'apply.dateOfBirth': 'Date of Birth',
    'apply.gender': 'Gender',
    'apply.gender.male': 'Male',
    'apply.gender.female': 'Female',
    'apply.gender.other': 'Others',
    'apply.mobileNumber': 'Mobile Number',
    'apply.email': 'Email',
    'apply.nationality': 'Nationality',
    'apply.countryOfBirth': 'Country of Birth',
    'apply.homeAddress': 'Home Address',
    'apply.addressLine1': 'Address Line 1',
    'apply.addressLine2': 'Address Line 2',
    'apply.city': 'City',
    'apply.stateRegion': 'State / Province / Region',
    'apply.postalCode': 'Postal Code',
    'apply.country': 'Country',
    'apply.countryPlaceholder': '-- Select country --',
    'apply.correspondenceAddress': 'Correspondence Address',
    'apply.sameAsHome': 'Same as home address',
    'apply.previousEducation': 'Details of Previous Education',
    'apply.qualification': 'Course / Qualification',
    'apply.startDate': 'Start Date',
    'apply.endDate': 'End Date',
    'apply.workExperience': 'Work Experience (Optional)',
    'apply.jobTitle': 'Job Title',
    'apply.employer': 'Employer',
    'apply.responsibility': 'Responsibility',
    'apply.supplyInfo': 'Supply Information for Application',
    'apply.fullName': 'Full Name',
    'apply.relationshipToYou': 'Relationship to you',
    'apply.contactNumber': 'Contact Number',
    'apply.documents': 'Important Documents',
    'apply.documentsHelpPrefix': 'Attach identification and supporting documents. If you cannot attach here, you can email them to',
    'apply.documentsHelpSuffix': 'after submission.',
    'apply.idDocument': 'ID (Passport / Driving Licence)',
    'apply.educationDocuments': 'Education Documents',
    'apply.experienceDocuments': 'Experience Documents',
    'apply.uploadPrompt': 'Click to upload',
    'apply.uploadFormats': 'PDF, JPG or PNG',
    'apply.disclaimer': 'Disclaimer',
    'apply.disclaimerText': 'This is a secure page and your data is encrypted so that it cannot be easily read by anyone who might intercept it between your computer and this website. The data is made available to academic and administrative departments within the Atlas Academy and is used for processing applications only. All data will be treated confidentially. We are committed to protecting your rights and privacy. Please note that the Atlas Academy is under no obligation to respond to any Application Forms which are not completed in full. Check your information carefully before submitting this form.',
    'apply.consentText': 'I confirm that the information provided is true and accurate, and I consent to the processing of my personal data for the purposes of this application.',
    'apply.submit': 'Submit Application',
    'apply.submitting': 'Submitting…',
    'apply.errorConsent': 'Please accept the disclaimer to continue.',
    'apply.errorSubmission': 'Submission failed. Please check your details and try again.',
    // ── Home page ──────────────────────────────────────────────────────
    'home.hero.eyebrow': 'Qualifications from OTHM & QUALIFI',
    'home.hero.title1': 'Your Gateway to',
    'home.hero.titleHighlight': 'Flexible',
    'home.hero.title2': 'Distance Learning',
    'home.hero.desc1': 'Study over 100 accredited online courses designed to fit around your life. No fixed timetable — learn whenever and wherever you choose.',
    'home.hero.desc2': 'Enrol today with a low deposit. Pay nothing further until next month.',
    'home.hero.searchPlaceholder': 'Search for a course...',
    'home.hero.searchBtn': 'Search',
    'home.feature.payment.title': 'Flexible Payment Plans',
    'home.feature.payment.desc': 'Enrol with a small deposit. Spread the remaining cost over easy monthly instalments.',
    'home.feature.pace.title': 'Study at Your Own Pace',
    'home.feature.pace.desc': 'No fixed deadlines or class times. Study whenever works for you, with up to 12 months to complete.',
    'home.feature.tutor.title': 'Full Tutor Support',
    'home.feature.tutor.desc': 'Your dedicated tutor is always available through our 24/7 online portal to guide your progress.',
    'home.career.eyebrow': 'Free Career Guidance',
    'home.career.title': 'Need Help Choosing Your Next Step?',
    'home.career.desc': 'Whether you want to progress in your current role, switch career paths, or return to work after a break, our experienced advisers are here to help — completely free of charge.',
    'home.career.point1': 'Personalised course recommendations',
    'home.career.point2': 'Career pathway advice',
    'home.career.point3': 'Help with funding and payment options',
    'home.career.point4': 'Guidance on entry requirements',
    'home.career.cta': 'Speak to an Adviser',
    'home.courses.eyebrow': 'Our Courses',
    'home.courses.title': 'Popular Qualifications',
    'home.courses.browseAll': 'Browse All Courses',
    'home.courses.viewAll': 'View All 100+ Courses',
    'home.testimonials.eyebrow': 'Student Stories',
    'home.testimonials.title': 'What Our Learners Say',
    'home.awarding.label': 'Atlas Academy in partnership with Direct Skills',
    'home.awarding.regulatedBody': 'Regulated Awarding Body',
    'home.awarding.ukQA': 'UK Quality Assurance',
    'home.cta.title': 'Ready to Start Your Learning Journey?',
    'home.cta.desc': 'Enrol today, pay a small deposit, and gain immediate access to your course materials. No waiting, no complicated sign-up.',
    'home.cta.browseCourses': 'Browse Courses',
    'home.cta.speakAdviser': 'Speak to an Adviser',
    'shared.badge.sale': 'SALE',
    // ── About page ─────────────────────────────────────────────────────
    'about.hero.eyebrow': 'About Us',
    'about.hero.title': 'Who We Are',
    'about.hero.desc': 'Atlas Academy is a UK-based distance learning provider dedicated to making accredited qualifications accessible to everyone, regardless of their background, location, or schedule.',
    'about.mission.eyebrow': 'Our Mission',
    'about.mission.title': 'Removing Barriers to Quality Education',
    'about.mission.para1': "We believe that where you live, what you earn, and how busy your life is should never prevent you from achieving a professional qualification. That's why we've built every aspect of our service around flexibility, affordability, and outstanding learner support.",
    'about.mission.para2': 'Since our founding, we have helped thousands of working adults, parents, and career-changers gain the credentials they need to progress. Our courses are fully online, self-paced, and supported every step of the way.',
    'about.mission.point1': 'Over 5,000 learners successfully qualified',
    'about.mission.point2': '94% course completion and pass rate',
    'about.mission.point3': 'Courses from awarding bodies including OTHM and QUALIFI',
    'about.mission.point4': 'No classroom attendance required',
    'about.values.eyebrow': 'Our Values',
    'about.values.title': 'What We Stand For',
    'about.values.accessibility.title': 'Accessibility',
    'about.values.accessibility.desc': 'Education should be available to everyone. We remove physical, financial, and logistical barriers to learning.',
    'about.values.quality.title': 'Quality',
    'about.values.quality.desc': 'Every qualification we offer is regulated and accredited. We never compromise on academic standards.',
    'about.values.learner.title': 'Learner Focus',
    'about.values.learner.desc': 'Our students come first. Every decision we make is guided by what is best for the learner.',
    'about.values.community.title': 'Community',
    'about.values.community.desc': 'We build lasting relationships with our students and remain invested in their long-term success.',
    'about.team.eyebrow': 'Our People',
    'about.team.title': 'Meet the Team',
    'about.team.desc': 'Experienced educators and professionals committed to your success.',
    'about.accreditations.label': 'Awarding Bodies We Work With',
    'about.cta.title': 'Start Learning with Us Today',
    'about.cta.desc': 'Join thousands of learners who have already taken the next step in their careers.',
    'about.cta.btn': 'Explore Our Courses',
    // ── Contact page ───────────────────────────────────────────────────
    'contact.hero.eyebrow': 'Get in Touch',
    'contact.hero.title': 'Contact Us',
    'contact.hero.desc': 'Our friendly student advisers are here to help with any questions about courses, enrolment, or payment options.',
    'contact.info.title': 'How to Reach Us',
    'contact.info.phone': 'Phone',
    'contact.info.email': 'Email',
    'contact.info.address': 'Address',
    'contact.info.hours': 'Office Hours',
    'contact.info.hoursValue': 'Mon–Fri: 9:00am – 5:30pm',
    'contact.info.portalHours': 'Online portal: 24/7',
    'contact.prefer.title': 'Prefer to speak to someone?',
    'contact.prefer.desc': 'Our advisers are available Monday to Friday to answer your questions and help you choose the right course.',
    'contact.prefer.cta': 'Call us now →',
    'contact.success.title': 'Message Received!',
    'contact.success.desc': 'Thank you for getting in touch. One of our student advisers will respond within one working day. A confirmation email has been sent to you.',
    'contact.success.another': 'Send another message',
    'contact.form.title': 'Send Us a Message',
    'contact.form.desc': "Fill in the form below and we'll get back to you as quickly as possible.",
    'contact.form.firstName': 'First Name',
    'contact.form.lastName': 'Last Name',
    'contact.form.emailAddress': 'Email Address',
    'contact.form.phoneNumber': 'Phone Number',
    'contact.form.subject': 'Subject',
    'contact.form.message': 'Message',
    'contact.form.selectTopic': 'Select a topic…',
    'contact.form.topic.courseEnquiry': 'Course Enquiry',
    'contact.form.topic.enrolment': 'Enrolment & Registration',
    'contact.form.topic.fees': 'Fees & Payment Plans',
    'contact.form.topic.support': 'Student Support',
    'contact.form.topic.tech': 'Technical Help',
    'contact.form.topic.general': 'General Enquiry',
    'contact.form.sending': 'Sending…',
    'contact.form.send': 'Send Message',
    'contact.form.error': 'Failed to send message. Please try again or call us directly.',
    // ── How It Works page ──────────────────────────────────────────────
    'hiw.hero.eyebrow': 'The Process',
    'hiw.hero.title': 'How It Works',
    'hiw.hero.desc': 'Getting started is simple. From choosing your course to receiving your certificate, we guide you every step of the way.',
    'hiw.step1.title': 'Choose Your Course',
    'hiw.step1.desc': 'Browse our full range of accredited qualifications. Use our filters to narrow down by subject area, level, or awarding body. Not sure where to start? Our advisers will point you in the right direction.',
    'hiw.step1.tip': 'Most learners start with a Level 3 qualification if they have some prior experience in their chosen field.',
    'hiw.step2.title': 'Enrol Online',
    'hiw.step2.desc': 'Complete our straightforward online registration form. Select your preferred payment option — you can pay in full or begin with a small deposit and spread the rest over monthly instalments.',
    'hiw.step2.tip': 'Your deposit secures your place immediately. No waiting — you gain portal access the same day.',
    'hiw.step3.title': 'Study at Your Own Pace',
    'hiw.step3.desc': 'Log in to your personal student portal and begin studying straight away. All learning materials, assessments, and resources are available 24/7. Your dedicated tutor is there whenever you need guidance.',
    'hiw.step3.tip': 'Most students dedicate around 8-10 hours per week. There are no mandatory sessions or deadlines.',
    'hiw.step4.title': 'Achieve Your Qualification',
    'hiw.step4.desc': 'Once you have completed all units and assessments, your work is verified by the awarding body. Your official, nationally recognised certificate is issued and posted directly to you.',
    'hiw.step4.tip': 'Our 94% pass rate reflects the quality of our tutor support and course materials.',
    'hiw.benefits.eyebrow': 'The Benefits',
    'hiw.benefits.title': 'Why Choose Distance Learning?',
    'hiw.benefit1.title': 'Study Around Your Life',
    'hiw.benefit1.desc': 'No commuting, no fixed timetables. Learn during your lunch break, in the evenings, or at weekends — whatever suits you best.',
    'hiw.benefit2.title': 'Earn While You Learn',
    'hiw.benefit2.desc': 'Continue in your current role while working towards your next qualification. Many of our students find their studies directly enhance their day-to-day work.',
    'hiw.benefit3.title': 'Save on Costs',
    'hiw.benefit3.desc': 'No accommodation, no travel, no parking. Distance learning is significantly more cost-effective than traditional classroom study.',
    'hiw.benefit4.title': 'Nationally Recognised Results',
    'hiw.benefit4.desc': 'Our qualifications carry the same weight as those obtained in a classroom. Employers and universities recognise them equally.',
    'hiw.benefit5.title': 'Learn at Your Own Speed',
    'hiw.benefit5.desc': 'Take your time on challenging topics and move quickly through areas you find straightforward. The course adapts to you, not the other way around.',
    'hiw.benefit6.title': 'Always-On Tutor Support',
    'hiw.benefit6.desc': 'You are never truly studying alone. Your tutor is accessible via our portal at any time, providing personalised guidance and feedback.',
    'hiw.faqs.eyebrow': 'Questions & Answers',
    'hiw.faqs.title': 'Frequently Asked Questions',
    'hiw.cta.title': 'Ready to Get Started?',
    'hiw.cta.desc': 'Browse our courses and enrol today with a low deposit.',
    'hiw.cta.viewCourses': 'View Courses',
    'hiw.cta.contact': 'Contact Us',
    // ── Pricing page ───────────────────────────────────────────────────
    'pricing.hero.eyebrow': 'Transparent Fees',
    'pricing.hero.title': 'Course Fees & Pricing',
    'pricing.hero.desc': 'All courses are individually priced based on the level and qualification. Flexible payment plans are available on every course, with no hidden fees.',
    'pricing.cards.title': 'Course Pricing by Level',
    'pricing.cards.desc': 'Prices vary by course. The ranges below give a general guide.',
    'pricing.cards.popular': 'Most Popular',
    'pricing.cards.priceNote': 'Prices vary by course within this level',
    'pricing.payment.eyebrow': 'Payment Options',
    'pricing.payment.title': 'Flexible Ways to Pay',
    'pricing.payment.full.title': 'Pay in Full',
    'pricing.payment.full.desc': 'Pay the entire course fee upfront. This is the simplest option and ensures no further payments are due.',
    'pricing.payment.deposit.title': 'Low-Deposit Plan',
    'pricing.payment.deposit.desc': 'Secure your place with a small deposit — often as little as £50. You will not be charged again until the following month.',
    'pricing.payment.installments.title': 'Monthly Instalments',
    'pricing.payment.installments.desc': "Spread the remaining balance over 2–6 months depending on the course. We'll set up a simple direct debit agreement.",
    'pricing.discuss.title': 'Need to Discuss Fees?',
    'pricing.discuss.desc': 'Our advisers are happy to help you find the most suitable payment arrangement. There is no pressure and no obligation — just straightforward, honest guidance.',
    'pricing.discuss.call': 'Call: +44 (0) 20 3355 5646',
    'pricing.discuss.enquiry': 'Send an Enquiry',
    'pricing.faqTitle': 'Fee-Related Questions',
    'pricing.faq1.q': 'Are there any hidden fees?',
    'pricing.faq1.a': 'No. The price you see for each course is the total cost. There are no registration fees, material charges, or surprise costs added at checkout.',
    'pricing.faq2.q': 'Is there a refund policy?',
    'pricing.faq2.a': 'Yes. You are entitled to a cooling-off period following enrolment. Please contact our team within 14 days if you wish to cancel. Terms and conditions apply.',
    'pricing.faq3.q': 'Can I switch courses after enrolment?',
    'pricing.faq3.a': 'In most cases, yes. Speak to your student adviser and we will do our best to transfer you to a more suitable programme.',
    'pricing.faq4.q': 'Do you offer any discounts?',
    'pricing.faq4.a': 'We run seasonal promotions throughout the year. Bundle discounts are also available when enrolling in more than one course at the same time.',
    'pricing.cta.title': 'Ready to Enrol?',
    'pricing.cta.desc': 'Choose your course and start with a small deposit today. Your future starts now.',
    'pricing.cta.btn': 'Browse All Courses',
    'plan.l2.name': 'Level 2 Courses',
    'plan.l2.desc': 'Introductory qualifications for those new to a sector or returning to study.',
    'plan.l3.name': 'Level 3 Courses',
    'plan.l3.desc': 'Career-advancing qualifications for practitioners and aspiring professionals.',
    'plan.l47.name': 'Level 4–7 Courses',
    'plan.l47.desc': 'Higher-level qualifications for senior roles, management, and specialist practice.',
    'plan.feature.materials': 'Full online course materials',
    'plan.feature.tutorSupport': 'Dedicated tutor support',
    'plan.feature.priorityTutor': 'Priority tutor support',
    'plan.feature.seniorTutor': 'Senior tutor support',
    'plan.feature.6months': 'Up to 6 months to complete',
    'plan.feature.12months': 'Up to 12 months to complete',
    'plan.feature.18months': 'Up to 18 months to complete',
    'plan.feature.digitalCert': 'Digital certificate upon completion',
    'plan.feature.ofqualCert': 'Official awarding-body certificate',
    'plan.feature.payment': 'Flexible payment plan available',
    'plan.feature.portal': 'Student portal access',
    'plan.feature.career': 'Career guidance included',
    'plan.feature.progression': 'Career and progression guidance',
    'plan.feature.priority': 'Priority enrolment support',
    // ── Student Support page ───────────────────────────────────────────
    'support.hero.eyebrow': "We're Here for You",
    'support.hero.title': 'Student Support',
    'support.hero.desc': "Studying from home doesn't mean studying alone. We provide comprehensive support at every stage of your learning journey.",
    'support.stats.portal': 'Portal Access',
    'support.stats.response': 'Tutor Response Time',
    'support.stats.tutor': 'Dedicated Tutor Support',
    'support.resources.eyebrow': 'Support Services',
    'support.resources.title': 'What Support Do We Offer?',
    'support.tutor.title': 'Tutor Support',
    'support.tutor.desc': 'Every student is paired with a dedicated specialist tutor from the day they enrol. Get feedback on assignments, guidance on tricky topics, and encouragement throughout your studies.',
    'support.tutor.cta': 'Learn more',
    'support.materials.title': 'Study Materials',
    'support.materials.desc': 'All course content is available through our secure online portal, including reading materials, practice assessments, and multimedia resources — accessible on any device.',
    'support.materials.cta': 'Explore courses',
    'support.assignments.title': 'Assignment Guidance',
    'support.assignments.desc': 'Unsure how to approach an assignment? We provide model answers, guidance documents, and one-to-one tutor sessions to help you submit your best work.',
    'support.assignments.cta': 'Get help',
    'support.adviser.title': 'Adviser Chat',
    'support.adviser.desc': "Our student advisers are available by phone and email Monday to Friday. Whether you have an enrolment question or need general support, we're here to help.",
    'support.adviser.cta': 'Contact us',
    'support.wellbeing.eyebrow': 'Student Wellbeing',
    'support.wellbeing.title': 'Your Wellbeing Matters',
    'support.wellbeing.desc': "We understand that adult learners often balance study alongside demanding work and home lives. Our student support team is trained to recognise when someone may need a little extra help, and we proactively reach out to ensure no one falls behind.",
    'support.wellbeing.point1': 'Personal welfare check-ins from your adviser',
    'support.wellbeing.point2': 'Extension requests handled with understanding',
    'support.wellbeing.point3': 'No judgment — just practical support',
    'support.wellbeing.point4': 'Signposting to additional resources when needed',
    'support.portal.eyebrow': 'Student Portal',
    'support.portal.title': 'Everything in One Place',
    'support.portal.desc': 'Our online learning portal gives you instant access to all your course materials, assignment submissions, tutor messages, and progress tracking. Available on desktop, tablet, and mobile.',
    'support.portal.feature1': 'Course materials',
    'support.portal.feature2': 'Tutor messaging',
    'support.portal.feature3': '24/7 access',
    'support.portal.feature4': 'Progress tracking',
    'support.portal.enrolled': 'Already enrolled?',
    'support.portal.enrolledDesc': 'Log in to your student portal to access your course materials and connect with your tutor.',
    'support.portal.accessBtn': 'Access Student Portal',
    'support.portal.notEnrolled': 'Not yet enrolled?',
    'support.portal.browse': 'Browse courses',
    'support.faqs.eyebrow': 'Help Centre',
    'support.faqs.title': 'Common Support Questions',
    'support.faqs.viewAll': 'View all FAQs',
    'support.cta.title': "Have a Question We Haven't Answered?",
    'support.cta.desc': "Our advisers are ready to help. Reach out and we'll respond promptly.",
    'support.cta.btn': 'Contact Student Support',
    // ── Awarding Body page ─────────────────────────────────────────────
    'awarding.chip': 'Partner Awarding Body',
    'awarding.chipLabel': 'Awarding Body',
    'awarding.avgRating': '4.8 average rating',
    'awarding.glance.title': 'At a Glance',
    'awarding.glance.body': 'Awarding body',
    'awarding.glance.regulator': 'Regulator',
    'awarding.glance.framework': 'Framework',
    'awarding.glance.levels': 'Levels offered',
    'awarding.glance.delivery': 'Delivery',
    'awarding.glance.deliveryValue': 'Online / Blended',
    'awarding.qualifications.eyebrow': 'Qualifications',
    'awarding.qualifications.viewAll': 'View all',
    'awarding.other.eyebrow': 'Also Available',
    'awarding.other.desc': "We deliver the full portfolio from both of the UK's leading vocational awarding bodies.",
    'awarding.trust': 'Atlas Academy delivers qualifications from recognised UK awarding bodies including OTHM and QUALIFI. Your certificate is issued by the relevant awarding body and carries recognised UK credit value.',
    // ── Courses page ───────────────────────────────────────────────────
    'courses.hero.eyebrow': 'Explore Our Qualifications',
    'courses.hero.title': 'Courses & Qualifications',
    'courses.hero.desc': 'Browse 100+ accredited distance learning courses across a wide range of sectors. All study is fully online, flexible, and tutor-supported.',
    'courses.search.placeholder': 'Search courses...',
    'courses.filters.btn': 'Filters',
    'courses.sort.featured': 'Featured First',
    'courses.sort.priceLow': 'Price: Low to High',
    'courses.sort.priceHigh': 'Price: High to Low',
    'courses.sort.rating': 'Highest Rated',
    'courses.filter.category': 'Category',
    'courses.filter.level': 'Level',
    'courses.filter.body': 'Awarding Body',
    'courses.filter.allLevels': 'All Levels',
    'courses.filter.allBodies': 'All Bodies',
    'courses.filter.clear': 'Clear all filters',
    'courses.loading': 'Loading courses…',
    'courses.notFound': 'No courses found matching your criteria.',
    'courses.clearFilters': 'Clear filters',
    // ── STATS ──────────────────────────────────────────────────────────
    'stats.courses': 'Accredited Courses',
    'stats.levels': 'Qualification Levels',
    'stats.portal': 'Online Portal Access',
    'stats.online': 'Online & Flexible Study',
    'shared.instalments.short': 'or {count} × £{amount}',
    // ── FAQs ───────────────────────────────────────────────────────────
    'faq.1.q': 'How does distance learning work?',
    'faq.1.a': 'All study materials are delivered through our secure online learning portal. Once enrolled, you will gain immediate access to your course content, including reading materials, assignments, and tutor feedback. You study entirely at your own pace, with no fixed timetable.',
    'faq.2.q': 'Are the qualifications officially recognised?',
    'faq.2.a': 'Yes. Our qualifications are awarded by recognised UK awarding bodies including OTHM and QUALIFI. They are widely valued by employers and progression providers across the UK and internationally.',
    'faq.3.q': 'How long do I have to complete my course?',
    'faq.3.a': 'Most courses allow up to 12 months for completion. Shorter programmes typically allow 6 months. If you need additional time, you can speak to your adviser about an extension.',
    'faq.4.q': 'What tutor support is available?',
    'faq.4.a': 'Every student is assigned a dedicated specialist tutor who can be contacted via our online portal, email, or phone. Tutors provide feedback on assignments, answer questions, and offer encouragement throughout your studies.',
    'faq.5.q': 'Can I enrol if I have no previous qualifications?',
    'faq.5.a': 'Many of our Level 2 and Level 3 courses do not require prior qualifications. Entry requirements vary by programme. Our advisers will assess your suitability and recommend the most appropriate starting point.',
    'faq.6.q': 'What payment options are available?',
    'faq.6.a': 'You can pay the full course fee upfront or spread the cost with one of our flexible payment plans. Most courses require only a small deposit to begin, with the remaining balance spread over monthly instalments.',
    'faq.7.q': 'Do I need any special equipment?',
    'faq.7.a': 'All you need is a reliable internet connection and a device such as a laptop, desktop computer, or tablet. No specialist software is required. Our portal is fully accessible from any modern web browser.',
    'faq.8.q': 'How will I be assessed?',
    'faq.8.a': 'Assessment methods vary by qualification but typically include written assignments, case studies, and portfolio work. There are no timed exams for most programmes. All work is submitted digitally through the student portal.',
    'faq.9.q': 'Can I study while working full-time?',
    'faq.9.a': 'Absolutely. Our courses are specifically designed for working adults. There are no set class times and all materials are available 24/7. Most students study for 8-10 hours per week, though this is entirely up to you.',
    'faq.10.q': 'What happens after I complete my qualification?',
    'faq.10.a': 'Upon successful completion, you will receive your official certificate from the awarding body. We also provide a career guidance service and can advise on pathways for further study or progression within your sector.',
  },
  ar: {
    'language.label': 'اللغة',
    'language.en': 'الإنجليزية',
    'language.ar': 'العربية',
    'language.fr': 'الفرنسية',
    'header.home': 'الرئيسية',
    'header.courses': 'الدورات',
    'header.universityProgression': 'التقدم إلى الجامعة',
    'header.applyNow': 'قدم الآن',
    'header.aboutUs': 'من نحن',
    'header.howItWorks': 'كيف يعمل',
    'header.studentSupport': 'دعم الطلاب',
    'header.feesPricing': 'الرسوم والأسعار',
    'header.contact': 'تواصل معنا',
    'header.studentPortal': 'بوابة الطالب',
    'header.allCourses': 'جميع الدورات',
    'header.awardingBodies': 'الجهات المانحة',
    'header.othmQualifications': 'مؤهلات OTHM',
    'header.qualifiQualifications': 'مؤهلات QUALIFI',
    'header.toggleMenu': 'تبديل القائمة',
    'footer.tagline': 'نمكّن المتعلمين في جميع أنحاء المملكة المتحدة من خلال برامج تعليم عن بعد مرنة ومعتمدة. ادرس بالوتيرة التي تناسبك وحقق أهدافك المهنية.',
    'footer.courseCategories': 'فئات الدورات',
    'footer.quickLinks': 'روابط سريعة',
    'footer.contactUs': 'اتصل بنا',
    'footer.accreditation': 'الجهات المانحة المتاحة عبر Atlas Academy هي: OTHM و QUALIFI.',
    'footer.privacyPolicy': 'سياسة الخصوصية',
    'footer.termsConditions': 'الشروط والأحكام',
    'footer.cookiePolicy': 'سياسة ملفات تعريف الارتباط',
    'footer.termsOfUse': 'شروط الاستخدام',
    'footer.rightsReserved': '© {year} Atlas Academy Ltd. جميع الحقوق محفوظة.',
    'course.backToCourses': 'العودة إلى الدورات',
    'course.notFound': 'لم يتم العثور على الدورة.',
    'course.students': 'طالب',
    'course.reviews': 'مراجعة',
    'course.aboutTitle': 'حول هذه الدورة',
    'course.aboutSupplement': 'يتم تقديم هذا المؤهل بالكامل عبر الإنترنت من خلال بوابة الطلاب الآمنة الخاصة بنا. ستحصل على إمكانية الوصول إلى جميع المواد الدراسية والتقييمات وملاحظات المدرس من اليوم الأول للتسجيل. لا توجد جداول زمنية ثابتة، بل تدرس وفقاً لظروفك.',
    'course.studyTitle': 'ماذا ستدرس',
    'course.detailsTitle': 'تفاصيل الدورة',
    'course.detail.level': 'المستوى',
    'course.detail.duration': 'المدة',
    'course.detail.studyMode': 'طريقة الدراسة',
    'course.detail.studyModeValue': 'عبر الإنترنت / تعليم عن بعد',
    'course.detail.awardingBody': 'الجهة المانحة',
    'course.detail.assessment': 'التقييم',
    'course.detail.assessmentValue': 'ملف إنجاز / مهام كتابية',
    'course.detail.certificate': 'الشهادة',
    'course.detail.certificateValue': 'مؤهل بريطاني رسمي',
    'course.paymentOptions': 'خيارات الدفع',
    'course.toComplete': 'لإكمال الدورة',
    'course.modulesCount': 'وحدات دراسية',
    'course.accreditedBy': 'معتمد من',
    'course.flexiblePayment': 'خطة دفع مرنة متاحة',
    'course.enrolNow': 'سجل الآن',
    'course.requestCallback': 'اطلب معاودة الاتصال',
    'course.paySecurely': 'ادفع بأمان',
    'course.paymentHelp': 'دفع آمن عبر Stripe.',
    'course.paymentSuccess': 'تم الدفع بنجاح. سيتواصل فريق التسجيل معك قريباً لتأكيد الخطوات التالية.',
    'course.paymentCancelled': 'تم إلغاء الدفع. يمكنك استئناف عملية الدفع في أي وقت.',
    'course.paymentError': 'تعذر بدء الدفع الآمن حالياً. يرجى المحاولة مرة أخرى لاحقاً.',
    'course.enquirySubmitted': 'تم إرسال الاستفسار!',
    'course.callbackSuccess': 'سيتواصل معك فريقنا خلال 24 ساعة لإكمال التسجيل.',
    'course.callbackTitle': 'اطلب معاودة الاتصال',
    'course.firstName': 'الاسم الأول',
    'course.lastName': 'اسم العائلة',
    'course.emailAddress': 'البريد الإلكتروني',
    'course.phoneNumber': 'رقم الهاتف',
    'course.sending': 'جارٍ الإرسال…',
    'course.submitEnquiry': 'إرسال الاستفسار',
    'course.cancel': 'إلغاء',
    'course.relatedCourses': 'دورات ذات صلة',
    'course.youSave': 'توفر £{amount}',
    'apply.heroEyebrow': 'نموذج تقديم عبر الإنترنت',
    'apply.heroTitle': 'قدم للدورة التي تريدها',
    'apply.heroDescription': 'يرجى تعبئة المعلومات المطلوبة في هذا النموذج بأكبر قدر ممكن من التفصيل حتى تتم معالجة طلبك بنجاح. يجب إكمال جميع الحقول المشار إليها بعلامة *.',
    'apply.applicationReceived': 'تم استلام الطلب',
    'apply.successIntro': 'شكراً لك {firstName}. تم إرسال طلبك للدورة {courseTitle} بنجاح.',
    'apply.successFollowup': 'سيتواصل أحد أعضاء فريق القبول خلال يوم عمل واحد للتحقق من بياناتك وإكمال تسجيلك. تم إرسال رسالة تأكيد إلى {email}.',
    'apply.browseMoreCourses': 'استعرض المزيد من الدورات',
    'apply.returnHome': 'العودة إلى الرئيسية',
    'apply.courseSelection': 'اختيار الدورة',
    'apply.selectCourse': 'اختر الدورة',
    'apply.loadingCourses': '-- جارٍ تحميل الدورات --',
    'apply.selectCoursePlaceholder': '-- اختر دورة --',
    'apply.currentCourseFee': 'رسوم الدورة الحالية: £{amount}',
    'apply.studyMode': 'طريقة الدراسة',
    'apply.studyMode.online': 'عبر الإنترنت',
    'apply.studyMode.blended': 'تعلم مدمج',
    'apply.feesMode': 'رسوم الدورة (الجهة الدافعة)',
    'apply.feesMode.selfFunded': 'تمويل ذاتي',
    'apply.feesMode.company': 'رعاية من الشركة',
    'apply.feesMode.loan': 'قرض بنكي',
    'apply.paymentMethod': 'طريقة الدفع',
    'apply.paymentMethod.full': 'الدفع بالكامل (دفعة واحدة)',
    'apply.paymentMethod.installments': 'أقساط شهرية',
    'apply.courseStartingDate': 'تاريخ بدء الدورة',
    'apply.previouslyStudied': 'هل درست سابقاً في Atlas Academy؟',
    'apply.heardAbout': 'كيف سمعت عنا لأول مرة؟',
    'apply.hear.google': 'جوجل',
    'apply.hear.bing': 'بينغ',
    'apply.hear.social': 'وسائل التواصل الاجتماعي',
    'apply.hear.referral': 'صديق / عائلة / إحالة',
    'apply.hear.other': 'أخرى (يرجى التحديد)',
    'apply.selectOption': 'اختر خياراً',
    'apply.other': 'أخرى',
    'apply.yes': 'نعم',
    'apply.no': 'لا',
    'apply.pleaseSpecify': 'يرجى التحديد',
    'apply.promoCode': 'رمز الخصم / رمز الإحالة (اختياري)',
    'apply.personalInformation': 'المعلومات الشخصية',
    'apply.title': 'اللقب',
    'apply.title.mr': 'السيد',
    'apply.title.mrs': 'السيدة',
    'apply.title.miss': 'الآنسة',
    'apply.title.ms': 'السيدة',
    'apply.title.dr': 'الدكتور',
    'apply.firstName': 'الاسم الأول',
    'apply.middleName': 'الاسم الأوسط',
    'apply.surname': 'اسم العائلة',
    'apply.dateOfBirth': 'تاريخ الميلاد',
    'apply.gender': 'الجنس',
    'apply.gender.male': 'ذكر',
    'apply.gender.female': 'أنثى',
    'apply.gender.other': 'أخرى',
    'apply.mobileNumber': 'رقم الجوال',
    'apply.email': 'البريد الإلكتروني',
    'apply.nationality': 'الجنسية',
    'apply.countryOfBirth': 'بلد الميلاد',
    'apply.homeAddress': 'عنوان السكن',
    'apply.addressLine1': 'سطر العنوان 1',
    'apply.addressLine2': 'سطر العنوان 2',
    'apply.city': 'المدينة',
    'apply.stateRegion': 'الولاية / المنطقة / الإقليم',
    'apply.postalCode': 'الرمز البريدي',
    'apply.country': 'الدولة',
    'apply.countryPlaceholder': '-- اختر الدولة --',
    'apply.correspondenceAddress': 'عنوان المراسلات',
    'apply.sameAsHome': 'نفس عنوان السكن',
    'apply.previousEducation': 'تفاصيل التعليم السابق',
    'apply.qualification': 'الدورة / المؤهل',
    'apply.startDate': 'تاريخ البدء',
    'apply.endDate': 'تاريخ الانتهاء',
    'apply.workExperience': 'الخبرة العملية (اختياري)',
    'apply.jobTitle': 'المسمى الوظيفي',
    'apply.employer': 'جهة العمل',
    'apply.responsibility': 'المسؤوليات',
    'apply.supplyInfo': 'معلومات الدعم للطلب',
    'apply.fullName': 'الاسم الكامل',
    'apply.relationshipToYou': 'صلة القرابة',
    'apply.contactNumber': 'رقم التواصل',
    'apply.documents': 'المستندات المهمة',
    'apply.documentsHelpPrefix': 'أرفق مستندات الهوية والدعم. إذا لم تتمكن من الإرفاق هنا، يمكنك إرسالها عبر البريد الإلكتروني إلى',
    'apply.documentsHelpSuffix': 'بعد إرسال الطلب.',
    'apply.idDocument': 'الهوية (جواز سفر / رخصة قيادة)',
    'apply.educationDocuments': 'المستندات التعليمية',
    'apply.experienceDocuments': 'مستندات الخبرة',
    'apply.uploadPrompt': 'اضغط للرفع',
    'apply.uploadFormats': 'PDF أو JPG أو PNG',
    'apply.disclaimer': 'إقرار',
    'apply.disclaimerText': 'هذه صفحة آمنة وبياناتك مشفرة بحيث لا يمكن قراءتها بسهولة من أي شخص قد يعترضها بين جهازك وهذا الموقع. يتم إتاحة البيانات للأقسام الأكاديمية والإدارية داخل Atlas Academy وتستخدم فقط لمعالجة الطلبات. سيتم التعامل مع جميع البيانات بسرية تامة. نحن ملتزمون بحماية حقوقك وخصوصيتك. يرجى ملاحظة أن Atlas Academy غير ملزم بالرد على أي نماذج طلب غير مكتملة بالكامل. تحقق من معلوماتك بعناية قبل إرسال هذا النموذج.',
    'apply.consentText': 'أؤكد أن المعلومات المقدمة صحيحة ودقيقة، وأوافق على معالجة بياناتي الشخصية لأغراض هذا الطلب.',
    'apply.submit': 'إرسال الطلب',
    'apply.submitting': 'جارٍ الإرسال…',
    'apply.errorConsent': 'يرجى قبول الإقرار للمتابعة.',
    'apply.errorSubmission': 'فشل الإرسال. يرجى التحقق من بياناتك والمحاولة مرة أخرى.',
    // ── Home page ──────────────────────────────────────────────────────
    'home.hero.eyebrow': 'مؤهلات من OTHM و QUALIFI',
    'home.hero.title1': 'بوابتك إلى',
    'home.hero.titleHighlight': 'مرن',
    'home.hero.title2': 'التعلم عن بعد',
    'home.hero.desc1': 'ادرس أكثر من 100 دورة معتمدة عبر الإنترنت مصممة لتناسب حياتك. لا جدول زمني ثابت — تعلم متى وأينما تشاء.',
    'home.hero.desc2': 'سجل اليوم بدفع وديعة بسيطة. لا تدفع شيئاً آخر حتى الشهر القادم.',
    'home.hero.searchPlaceholder': 'ابحث عن دورة...',
    'home.hero.searchBtn': 'بحث',
    'home.feature.payment.title': 'خطط دفع مرنة',
    'home.feature.payment.desc': 'سجل بدفع وديعة بسيطة. وزّع التكلفة المتبقية على أقساط شهرية ميسّرة.',
    'home.feature.pace.title': 'ادرس بوتيرتك الخاصة',
    'home.feature.pace.desc': 'لا مواعيد نهائية ثابتة ولا أوقات دروس. ادرس متى يناسبك مع مدة تصل إلى 12 شهراً.',
    'home.feature.tutor.title': 'دعم تدريسي كامل',
    'home.feature.tutor.desc': 'مدرسك المخصص متاح دائماً عبر بوابتنا الإلكترونية على مدار الساعة لإرشادك.',
    'home.career.eyebrow': 'إرشاد مهني مجاني',
    'home.career.title': 'هل تحتاج مساعدة في اختيار خطوتك التالية؟',
    'home.career.desc': 'سواء كنت ترغب في التقدم في دورك الحالي، أو تغيير مسارك المهني، أو العودة إلى العمل، مستشارونا هنا للمساعدة — مجاناً.',
    'home.career.point1': 'توصيات دورات شخصية',
    'home.career.point2': 'نصائح مسار مهني',
    'home.career.point3': 'مساعدة في خيارات التمويل والدفع',
    'home.career.point4': 'إرشاد حول متطلبات الالتحاق',
    'home.career.cta': 'تحدث إلى مستشار',
    'home.courses.eyebrow': 'دوراتنا',
    'home.courses.title': 'المؤهلات الشائعة',
    'home.courses.browseAll': 'تصفح جميع الدورات',
    'home.courses.viewAll': 'عرض أكثر من 100 دورة',
    'home.testimonials.eyebrow': 'قصص الطلاب',
    'home.testimonials.title': 'ما يقوله متعلمونا',
    'home.awarding.label': 'الجهات المانحة التي نتعاون معها',
    'home.awarding.regulatedBody': 'جهة منح معتمدة',
    'home.awarding.ukQA': 'ضمان الجودة البريطاني',
    'home.cta.title': 'هل أنت مستعد لبدء رحلتك التعليمية؟',
    'home.cta.desc': 'سجل اليوم، ادفع وديعة بسيطة، واحصل على وصول فوري لمواد دورتك. لا انتظار، لا تسجيل معقد.',
    'home.cta.browseCourses': 'تصفح الدورات',
    'home.cta.speakAdviser': 'تحدث إلى مستشار',
    'shared.badge.sale': 'تخفيض',
    // ── About page ─────────────────────────────────────────────────────
    'about.hero.eyebrow': 'من نحن',
    'about.hero.title': 'من نحن',
    'about.hero.desc': 'Atlas Academy هي مزود تعليم عن بعد مقره المملكة المتحدة، ملتزم بجعل المؤهلات المعتمدة في متناول الجميع، بصرف النظر عن خلفياتهم أو مواقعهم أو جداولهم.',
    'about.mission.eyebrow': 'مهمتنا',
    'about.mission.title': 'إزالة العوائق أمام التعليم الجيد',
    'about.mission.para1': 'نؤمن بأن مكان إقامتك، ودخلك، وانشغالاتك لا ينبغي أن تحول دون حصولك على مؤهل مهني. لهذا بنينا كل جانب من جوانب خدمتنا حول المرونة والقدرة على تحمل التكاليف والدعم الاستثنائي للمتعلمين.',
    'about.mission.para2': 'منذ تأسيسنا، ساعدنا آلاف البالغين العاملين والآباء والراغبين في تغيير مساراتهم المهنية على الحصول على المؤهلات التي يحتاجونها للتقدم. دوراتنا متاحة بالكامل عبر الإنترنت، ويمكن دراستها بوتيرتك الخاصة مع دعم كامل في كل مرحلة.',
    'about.mission.point1': 'أكثر من 5000 متعلم حصل على مؤهلاته بنجاح',
    'about.mission.point2': 'نسبة إكمال الدورات والنجاح 94%',
    'about.mission.point3': 'دورات من جهات مانحة تشمل OTHM و QUALIFI',
    'about.mission.point4': 'لا يُشترط الحضور في الفصول الدراسية',
    'about.values.eyebrow': 'قيمنا',
    'about.values.title': 'ما نؤمن به',
    'about.values.accessibility.title': 'إمكانية الوصول',
    'about.values.accessibility.desc': 'التعليم يجب أن يكون متاحاً للجميع. نزيل الحواجز الجسدية والمالية واللوجستية أمام التعلم.',
    'about.values.quality.title': 'الجودة',
    'about.values.quality.desc': 'كل مؤهل نقدمه منظم ومعتمد. لا نتنازل أبداً عن المعايير الأكاديمية.',
    'about.values.learner.title': 'التركيز على المتعلم',
    'about.values.learner.desc': 'طلابنا في المقام الأول. كل قرار نتخذه يسترشد بما هو أفضل للمتعلم.',
    'about.values.community.title': 'المجتمع',
    'about.values.community.desc': 'نبني علاقات دائمة مع طلابنا ونظل مهتمين بنجاحهم على المدى البعيد.',
    'about.team.eyebrow': 'فريقنا',
    'about.team.title': 'تعرّف على الفريق',
    'about.team.desc': 'مربّون ومهنيون ذوو خبرة ملتزمون بنجاحك.',
    'about.accreditations.label': 'الجهات المانحة التي نعمل معها',
    'about.cta.title': 'ابدأ التعلم معنا اليوم',
    'about.cta.desc': 'انضم إلى آلاف المتعلمين الذين خطوا بالفعل خطوتهم التالية في مسيرتهم المهنية.',
    'about.cta.btn': 'استعرض دوراتنا',
    // ── Contact page ───────────────────────────────────────────────────
    'contact.hero.eyebrow': 'تواصل معنا',
    'contact.hero.title': 'اتصل بنا',
    'contact.hero.desc': 'مستشارو الطلاب الودودون لدينا هنا للمساعدة في أي أسئلة حول الدورات أو التسجيل أو خيارات الدفع.',
    'contact.info.title': 'كيفية التواصل معنا',
    'contact.info.phone': 'هاتف',
    'contact.info.email': 'البريد الإلكتروني',
    'contact.info.address': 'العنوان',
    'contact.info.hours': 'ساعات العمل',
    'contact.info.hoursValue': 'الاثنين–الجمعة: 9:00ص – 5:30م',
    'contact.info.portalHours': 'البوابة الإلكترونية: 24/7',
    'contact.prefer.title': 'هل تفضل التحدث مع شخص؟',
    'contact.prefer.desc': 'مستشارونا متاحون من الاثنين إلى الجمعة للإجابة على أسئلتك ومساعدتك في اختيار الدورة المناسبة.',
    'contact.prefer.cta': 'اتصل بنا الآن ←',
    'contact.success.title': 'تم استلام رسالتك!',
    'contact.success.desc': 'شكراً لتواصلك معنا. سيرد عليك أحد مستشاري الطلاب لدينا خلال يوم عمل واحد. تم إرسال بريد إلكتروني تأكيدي إليك.',
    'contact.success.another': 'إرسال رسالة أخرى',
    'contact.form.title': 'أرسل لنا رسالة',
    'contact.form.desc': 'أكمل النموذج أدناه وسنرد عليك في أقرب وقت ممكن.',
    'contact.form.firstName': 'الاسم الأول',
    'contact.form.lastName': 'اسم العائلة',
    'contact.form.emailAddress': 'البريد الإلكتروني',
    'contact.form.phoneNumber': 'رقم الهاتف',
    'contact.form.subject': 'الموضوع',
    'contact.form.message': 'الرسالة',
    'contact.form.selectTopic': 'اختر موضوعاً…',
    'contact.form.topic.courseEnquiry': 'استفسار عن دورة',
    'contact.form.topic.enrolment': 'التسجيل والالتحاق',
    'contact.form.topic.fees': 'الرسوم وخطط الدفع',
    'contact.form.topic.support': 'دعم الطلاب',
    'contact.form.topic.tech': 'مساعدة تقنية',
    'contact.form.topic.general': 'استفسار عام',
    'contact.form.sending': 'جارٍ الإرسال…',
    'contact.form.send': 'إرسال الرسالة',
    'contact.form.error': 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.',
    // ── How It Works page ──────────────────────────────────────────────
    'hiw.hero.eyebrow': 'العملية',
    'hiw.hero.title': 'كيف يعمل',
    'hiw.hero.desc': 'البدء بسيط. من اختيار دورتك إلى استلام شهادتك، نرشدك في كل خطوة على الطريق.',
    'hiw.step1.title': 'اختر دورتك',
    'hiw.step1.desc': 'تصفح مجموعتنا الكاملة من المؤهلات المعتمدة. استخدم الفلاتر للتضييق حسب المجال أو المستوى أو الجهة المانحة. لست متأكداً من أين تبدأ؟ سيرشدك مستشارونا في الاتجاه الصحيح.',
    'hiw.step1.tip': 'يبدأ معظم المتعلمين بمؤهل المستوى 3 إذا كان لديهم بعض الخبرة المسبقة في مجالهم المختار.',
    'hiw.step2.title': 'سجّل عبر الإنترنت',
    'hiw.step2.desc': 'أكمل نموذج التسجيل الإلكتروني البسيط. اختر خيار الدفع المفضل — يمكنك الدفع بالكامل أو البدء بوديعة صغيرة وتوزيع الباقي على أقساط شهرية.',
    'hiw.step2.tip': 'تؤمّن وديعتك مقعدك فوراً. لا انتظار — تحصل على صلاحية الوصول للبوابة في نفس اليوم.',
    'hiw.step3.title': 'ادرس بوتيرتك الخاصة',
    'hiw.step3.desc': 'سجّل الدخول إلى بوابتك الشخصية للطالب وابدأ الدراسة فوراً. جميع المواد التعليمية والتقييمات والموارد متاحة على مدار الساعة طوال أيام الأسبوع. مدرسك المخصص حاضر كلما احتجت توجيهاً.',
    'hiw.step3.tip': 'يخصص معظم الطلاب حوالي 8-10 ساعات أسبوعياً. لا توجد جلسات إلزامية أو مواعيد نهائية.',
    'hiw.step4.title': 'احصل على مؤهلك',
    'hiw.step4.desc': 'بعد إكمال جميع الوحدات والتقييمات، يتم التحقق من عملك من قبل الجهة المانحة. تُصدر شهادتك الرسمية المعترف بها وطنياً وترسل مباشرة إليك.',
    'hiw.step4.tip': 'نسبة النجاح 94% لدينا تعكس جودة دعم المدرسين ومواد الدورات.',
    'hiw.benefits.eyebrow': 'الفوائد',
    'hiw.benefits.title': 'لماذا تختار التعلم عن بعد؟',
    'hiw.benefit1.title': 'ادرس حول حياتك',
    'hiw.benefit1.desc': 'لا تنقل، لا جداول ثابتة. تعلم خلال استراحة الغداء، أو في المساء، أو في عطلة نهاية الأسبوع — أياً كان ما يناسبك.',
    'hiw.benefit2.title': 'اكسب بينما تتعلم',
    'hiw.benefit2.desc': 'استمر في دورك الحالي بينما تعمل نحو مؤهلك التالي. يجد كثير من طلابنا أن دراستهم تعزز مباشرة عملهم اليومي.',
    'hiw.benefit3.title': 'وفّر في التكاليف',
    'hiw.benefit3.desc': 'لا سكن، لا سفر، لا مواقف. التعلم عن بعد أكثر فعالية من حيث التكلفة بشكل ملحوظ مقارنة بالدراسة التقليدية.',
    'hiw.benefit4.title': 'نتائج معترف بها وطنياً',
    'hiw.benefit4.desc': 'تحمل مؤهلاتنا نفس القيمة التي تحملها تلك المكتسبة في الفصل الدراسي. يعترف بها أصحاب العمل والجامعات على حد سواء.',
    'hiw.benefit5.title': 'تعلم بسرعتك الخاصة',
    'hiw.benefit5.desc': 'خذ وقتك في المواضيع الصعبة وتقدم بسرعة في المجالات التي تجدها سهلة. الدورة تتكيف معك، وليس العكس.',
    'hiw.benefit6.title': 'دعم تدريسي دائم',
    'hiw.benefit6.desc': 'لا تدرس وحدك أبداً. مدرسك متاح عبر بوابتنا في أي وقت، يقدم إرشادات وتغذية راجعة شخصية.',
    'hiw.faqs.eyebrow': 'أسئلة وأجوبة',
    'hiw.faqs.title': 'الأسئلة الشائعة',
    'hiw.cta.title': 'هل أنت مستعد للبدء؟',
    'hiw.cta.desc': 'تصفح دوراتنا وسجل اليوم بدفع وديعة بسيطة.',
    'hiw.cta.viewCourses': 'عرض الدورات',
    'hiw.cta.contact': 'تواصل معنا',
    // ── Pricing page ───────────────────────────────────────────────────
    'pricing.hero.eyebrow': 'رسوم شفافة',
    'pricing.hero.title': 'رسوم الدورات والأسعار',
    'pricing.hero.desc': 'تُسعَّر جميع الدورات بشكل فردي بناءً على المستوى والمؤهل. خطط دفع مرنة متاحة لكل دورة دون رسوم خفية.',
    'pricing.cards.title': 'أسعار الدورات حسب المستوى',
    'pricing.cards.desc': 'تتفاوت الأسعار حسب الدورة. النطاقات أدناه دليل عام.',
    'pricing.cards.popular': 'الأكثر شعبية',
    'pricing.cards.priceNote': 'الأسعار تتفاوت حسب الدورة ضمن هذا المستوى',
    'pricing.payment.eyebrow': 'خيارات الدفع',
    'pricing.payment.title': 'طرق دفع مرنة',
    'pricing.payment.full.title': 'الدفع الكامل',
    'pricing.payment.full.desc': 'ادفع رسوم الدورة كاملة مقدماً. هذا أبسط الخيارات ويضمن عدم وجود مدفوعات إضافية.',
    'pricing.payment.deposit.title': 'خطة الوديعة المنخفضة',
    'pricing.payment.deposit.desc': 'احجز مقعدك بوديعة صغيرة — في أحيان كثيرة بقدر £50. لن تُخصم أي مبالغ إضافية حتى الشهر التالي.',
    'pricing.payment.installments.title': 'أقساط شهرية',
    'pricing.payment.installments.desc': 'وزّع الرصيد المتبقي على 2–6 أشهر حسب الدورة. سنُنشئ اتفاقية خصم مباشر بسيطة.',
    'pricing.discuss.title': 'هل تريد مناقشة الرسوم؟',
    'pricing.discuss.desc': 'مستشارونا سعداء بمساعدتك في إيجاد أنسب ترتيب للدفع. لا ضغط ولا التزام — مجرد إرشادات صريحة وصادقة.',
    'pricing.discuss.call': 'اتصل: 5646 3355 20 44+',
    'pricing.discuss.enquiry': 'أرسل استفساراً',
    'pricing.faqTitle': 'أسئلة متعلقة بالرسوم',
    'pricing.faq1.q': 'هل توجد رسوم خفية؟',
    'pricing.faq1.a': 'لا. السعر الذي تراه لكل دورة هو التكلفة الإجمالية. لا رسوم تسجيل، ولا رسوم مواد، ولا تكاليف مفاجئة عند الدفع.',
    'pricing.faq2.q': 'هل توجد سياسة استرداد؟',
    'pricing.faq2.a': 'نعم. يحق لك فترة تهدئة بعد التسجيل. يرجى التواصل مع فريقنا خلال 14 يوماً إذا كنت ترغب في الإلغاء. تسري الشروط والأحكام.',
    'pricing.faq3.q': 'هل يمكنني تغيير الدورة بعد التسجيل؟',
    'pricing.faq3.a': 'في معظم الحالات نعم. تحدث مع مستشار الطالب وسنبذل قصارى جهدنا لنقلك إلى برنامج أكثر ملاءمة.',
    'pricing.faq4.q': 'هل تقدمون أي خصومات؟',
    'pricing.faq4.a': 'نُجري عروضاً موسمية على مدار العام. كما تتوفر خصومات الحزمة عند التسجيل في أكثر من دورة في نفس الوقت.',
    'pricing.cta.title': 'هل أنت مستعد للتسجيل؟',
    'pricing.cta.desc': 'اختر دورتك وابدأ بدفع وديعة صغيرة اليوم. مستقبلك يبدأ الآن.',
    'pricing.cta.btn': 'تصفح جميع الدورات',
    'plan.l2.name': 'دورات المستوى 2',
    'plan.l2.desc': 'مؤهلات تمهيدية لمن هم جدد في القطاع أو يعودون إلى الدراسة.',
    'plan.l3.name': 'دورات المستوى 3',
    'plan.l3.desc': 'مؤهلات تُعزز المسيرة المهنية للممارسين والمحترفين الطامحين.',
    'plan.l47.name': 'دورات المستويات 4–7',
    'plan.l47.desc': 'مؤهلات رفيعة المستوى للأدوار الإدارية العليا والمتخصصة.',
    'plan.feature.materials': 'مواد دراسية كاملة عبر الإنترنت',
    'plan.feature.tutorSupport': 'دعم مدرب مخصص',
    'plan.feature.priorityTutor': 'دعم مدرب ذو أولوية',
    'plan.feature.seniorTutor': 'دعم مدرب أول',
    'plan.feature.6months': 'ما يصل إلى 6 أشهر للإكمال',
    'plan.feature.12months': 'ما يصل إلى 12 شهراً للإكمال',
    'plan.feature.18months': 'ما يصل إلى 18 شهراً للإكمال',
    'plan.feature.digitalCert': 'شهادة رقمية عند الإكمال',
    'plan.feature.ofqualCert': 'شهادة رسمية من الجهة المانحة',
    'plan.feature.payment': 'خطة دفع مرنة متاحة',
    'plan.feature.portal': 'صلاحية الوصول لبوابة الطالب',
    'plan.feature.career': 'إرشاد مهني مضمّن',
    'plan.feature.progression': 'إرشاد مهني وإرشاد للتقدم',
    'plan.feature.priority': 'دعم تسجيل ذو أولوية',
    // ── Student Support page ───────────────────────────────────────────
    'support.hero.eyebrow': 'نحن هنا من أجلك',
    'support.hero.title': 'دعم الطلاب',
    'support.hero.desc': 'الدراسة من المنزل لا تعني الدراسة وحدك. نقدم دعماً شاملاً في كل مرحلة من رحلتك التعليمية.',
    'support.stats.portal': 'الوصول للبوابة',
    'support.stats.response': 'وقت رد المدرب',
    'support.stats.tutor': 'دعم مدرب مخصص',
    'support.resources.eyebrow': 'خدمات الدعم',
    'support.resources.title': 'ما الدعم الذي نقدمه؟',
    'support.tutor.title': 'دعم المدرب',
    'support.tutor.desc': 'يُقرن كل طالب بمدرب متخصص مخصص من يوم التسجيل. احصل على ملاحظات على الواجبات وإرشادات حول المواضيع الصعبة وتشجيع طوال دراستك.',
    'support.tutor.cta': 'اعرف المزيد',
    'support.materials.title': 'المواد الدراسية',
    'support.materials.desc': 'جميع محتويات الدورة متاحة عبر بوابتنا الآمنة عبر الإنترنت، بما في ذلك مواد القراءة والتقييمات التدريبية والموارد المتعددة — يمكن الوصول إليها من أي جهاز.',
    'support.materials.cta': 'استعرض الدورات',
    'support.assignments.title': 'إرشادات الواجبات',
    'support.assignments.desc': 'غير متأكد من كيفية التعامل مع واجب ما؟ نوفر نماذج إجابات ووثائق إرشادية وجلسات فردية مع المدرب لمساعدتك في تقديم أفضل أعمالك.',
    'support.assignments.cta': 'احصل على مساعدة',
    'support.adviser.title': 'محادثة المستشار',
    'support.adviser.desc': 'مستشارو الطلاب متاحون عبر الهاتف والبريد الإلكتروني من الاثنين إلى الجمعة. سواء كان لديك سؤال حول التسجيل أو تحتاج دعماً عاماً، نحن هنا.',
    'support.adviser.cta': 'تواصل معنا',
    'support.wellbeing.eyebrow': 'رفاهية الطالب',
    'support.wellbeing.title': 'رفاهيتك تهمنا',
    'support.wellbeing.desc': 'نتفهم أن المتعلمين البالغين كثيراً ما يوازنون بين الدراسة ومتطلبات العمل والحياة المنزلية. فريق دعم الطلاب لدينا مُدرَّب على التعرف على من قد يحتاج مساعدة إضافية، ونتواصل بشكل استباقي للتأكد من عدم تخلف أحد.',
    'support.wellbeing.point1': 'متابعة شخصية للرفاهية من مستشارك',
    'support.wellbeing.point2': 'طلبات التمديد تُعالج بتفهم',
    'support.wellbeing.point3': 'لا أحكام — فقط دعم عملي',
    'support.wellbeing.point4': 'توجيه إلى موارد إضافية عند الحاجة',
    'support.portal.eyebrow': 'بوابة الطالب',
    'support.portal.title': 'كل شيء في مكان واحد',
    'support.portal.desc': 'تمنحك بوابتنا للتعلم عبر الإنترنت وصولاً فورياً إلى جميع مواد دورتك وتقديم الواجبات ورسائل المدرب وتتبع التقدم. متاحة على سطح المكتب والجهاز اللوحي والهاتف المحمول.',
    'support.portal.feature1': 'مواد الدورة',
    'support.portal.feature2': 'مراسلة المدرب',
    'support.portal.feature3': 'الوصول على مدار الساعة',
    'support.portal.feature4': 'تتبع التقدم',
    'support.portal.enrolled': 'هل أنت مسجل بالفعل؟',
    'support.portal.enrolledDesc': 'سجّل الدخول إلى بوابتك الطلابية للوصول إلى مواد دورتك والتواصل مع مدربك.',
    'support.portal.accessBtn': 'الدخول إلى بوابة الطالب',
    'support.portal.notEnrolled': 'لم تسجل بعد؟',
    'support.portal.browse': 'تصفح الدورات',
    'support.faqs.eyebrow': 'مركز المساعدة',
    'support.faqs.title': 'أسئلة الدعم الشائعة',
    'support.faqs.viewAll': 'عرض جميع الأسئلة الشائعة',
    'support.cta.title': 'هل لديك سؤال لم نجب عنه؟',
    'support.cta.desc': 'مستشارونا مستعدون للمساعدة. تواصل معنا وسنرد بسرعة.',
    'support.cta.btn': 'التواصل مع دعم الطلاب',
    // ── Awarding Body page ─────────────────────────────────────────────
    'awarding.chip': 'جهة مانحة شريكة',
    'awarding.chipLabel': 'الجهة المانحة',
    'awarding.avgRating': 'متوسط تقييم 4.8',
    'awarding.glance.title': 'لمحة سريعة',
    'awarding.glance.body': 'الجهة المانحة',
    'awarding.glance.regulator': 'الجهة المنظمة',
    'awarding.glance.framework': 'الإطار',
    'awarding.glance.levels': 'المستويات المتاحة',
    'awarding.glance.delivery': 'طريقة التدريس',
    'awarding.glance.deliveryValue': 'عبر الإنترنت / مدمج',
    'awarding.qualifications.eyebrow': 'المؤهلات',
    'awarding.qualifications.viewAll': 'عرض الكل',
    'awarding.other.eyebrow': 'متاح أيضاً',
    'awarding.other.desc': 'نقدم المحفظة الكاملة من كلتا الجهتين المانحتين الرائدتين في المهنية في المملكة المتحدة.',
    'awarding.trust': 'تقدم Atlas Academy مؤهلات من جهات مانحة بريطانية معترف بها، بما في ذلك OTHM و QUALIFI. تُصدر شهادتك من الجهة المانحة ذات الصلة وتحمل قيمة اعتماد بريطانية معترفاً بها.',
    // ── Courses page ───────────────────────────────────────────────────
    'courses.hero.eyebrow': 'استكشف مؤهلاتنا',
    'courses.hero.title': 'الدورات والمؤهلات',
    'courses.hero.desc': 'تصفح أكثر من 100 دورة تعليم عن بعد معتمدة في مجموعة واسعة من القطاعات. جميع الدراسات متاحة بالكامل عبر الإنترنت، مرنة، ومدعومة بمدرب.',
    'courses.search.placeholder': 'ابحث عن الدورات...',
    'courses.filters.btn': 'الفلاتر',
    'courses.sort.featured': 'المميزة أولاً',
    'courses.sort.priceLow': 'السعر: من الأقل للأعلى',
    'courses.sort.priceHigh': 'السعر: من الأعلى للأقل',
    'courses.sort.rating': 'الأعلى تقييماً',
    'courses.filter.category': 'الفئة',
    'courses.filter.level': 'المستوى',
    'courses.filter.body': 'الجهة المانحة',
    'courses.filter.allLevels': 'جميع المستويات',
    'courses.filter.allBodies': 'جميع الجهات',
    'courses.filter.clear': 'مسح جميع الفلاتر',
    'courses.loading': 'جارٍ تحميل الدورات…',
    'courses.notFound': 'لم يتم العثور على دورات تطابق معاييرك.',
    'courses.clearFilters': 'مسح الفلاتر',
    // ── STATS ──────────────────────────────────────────────────────────
    'stats.courses': 'دورات معتمدة',
    'stats.levels': 'مستويات المؤهلات',
    'stats.portal': 'الوصول للبوابة الإلكترونية',
    'stats.online': 'دراسة مرنة عبر الإنترنت',
    'shared.instalments.short': 'أو {count} × {amount}£',
    // ── FAQs ───────────────────────────────────────────────────────────
    'faq.1.q': 'كيف يعمل التعلم عن بعد؟',
    'faq.1.a': 'تُقدَّم جميع المواد الدراسية عبر بوابة التعلم الإلكترونية الآمنة الخاصة بنا. بمجرد التسجيل، ستحصل على وصول فوري لمحتوى دورتك، بما في ذلك مواد القراءة والواجبات وملاحظات المدرب. تدرس بالكامل بوتيرتك الخاصة دون جدول زمني ثابت.',
    'faq.2.q': 'هل المؤهلات معترف بها رسمياً؟',
    'faq.2.a': 'نعم. تُمنح مؤهلاتنا من جهات مانحة بريطانية معترف بها تشمل OTHM و QUALIFI. وهي محل تقدير واسع لدى أصحاب العمل وجهات التقدم الأكاديمي في المملكة المتحدة وعلى المستوى الدولي.',
    'faq.3.q': 'كم من الوقت لدي لإكمال دورتي؟',
    'faq.3.a': 'تتيح معظم الدورات ما يصل إلى 12 شهراً للإكمال. تتيح البرامج الأقصر عادةً 6 أشهر. إذا كنت تحتاج وقتاً إضافياً، يمكنك التحدث مع مستشارك بشأن التمديد.',
    'faq.4.q': 'ما هو الدعم التدريسي المتاح؟',
    'faq.4.a': 'يُعيَّن لكل طالب مدرب متخصص مخصص يمكن التواصل معه عبر بوابتنا الإلكترونية أو البريد الإلكتروني أو الهاتف. يقدم المدربون ملاحظات على الواجبات ويجيبون على الأسئلة ويقدمون التشجيع طوال دراستك.',
    'faq.5.q': 'هل يمكنني التسجيل إذا لم يكن لدي مؤهلات سابقة؟',
    'faq.5.a': 'لا تتطلب كثير من دورات المستوى 2 والمستوى 3 لدينا مؤهلات مسبقة. تتفاوت متطلبات الالتحاق حسب البرنامج. سيقيّم مستشارونا ملاءمتك ويوصون بنقطة البداية الأنسب.',
    'faq.6.q': 'ما هي خيارات الدفع المتاحة؟',
    'faq.6.a': 'يمكنك دفع رسوم الدورة الكاملة مقدماً أو توزيع التكلفة بإحدى خطط الدفع المرنة لدينا. تتطلب معظم الدورات وديعة صغيرة فقط للبدء، مع توزيع الرصيد المتبقي على أقساط شهرية.',
    'faq.7.q': 'هل أحتاج معدات خاصة؟',
    'faq.7.a': 'كل ما تحتاجه هو اتصال إنترنت موثوق وجهاز مثل لابتوب أو حاسوب مكتبي أو جهاز لوحي. لا يلزم أي برنامج متخصص. بوابتنا متاحة بالكامل من أي متصفح إنترنت حديث.',
    'faq.8.q': 'كيف سيتم تقييمي؟',
    'faq.8.a': 'تتفاوت طرق التقييم حسب المؤهل لكنها عادةً تشمل الواجبات الكتابية ودراسات الحالة وأعمال المحفظة. لا توجد امتحانات محددة بوقت في معظم البرامج. تُقدَّم جميع الأعمال رقمياً عبر بوابة الطالب.',
    'faq.9.q': 'هل يمكنني الدراسة أثناء العمل بدوام كامل؟',
    'faq.9.a': 'بالتأكيد. دوراتنا مصممة خصيصاً للبالغين العاملين. لا توجد أوقات محددة للدروس وجميع المواد متاحة على مدار الساعة. يدرس معظم الطلاب 8-10 ساعات أسبوعياً، وإن كان ذلك متروكاً تماماً لك.',
    'faq.10.q': 'ماذا يحدث بعد إكمال مؤهلي؟',
    'faq.10.a': 'عند الإكمال الناجح، ستتلقى شهادتك الرسمية من الجهة المانحة. نقدم أيضاً خدمة إرشاد مهني ويمكننا إرشادك إلى مسارات الدراسة الإضافية أو التقدم داخل قطاعك.',
  },
  fr: {
    'language.label': 'Langue',
    'language.en': 'Anglais',
    'language.ar': 'Arabe',
    'language.fr': 'Français',
    'header.home': 'Accueil',
    'header.courses': 'Cours',
    'header.universityProgression': 'Progression universitaire',
    'header.applyNow': 'Postuler maintenant',
    'header.aboutUs': 'À propos de nous',
    'header.howItWorks': 'Comment ça marche',
    'header.studentSupport': 'Soutien aux étudiants',
    'header.feesPricing': 'Frais et tarifs',
    'header.contact': 'Contact',
    'header.studentPortal': 'Portail étudiant',
    'header.allCourses': 'Tous les cours',
    'header.awardingBodies': 'Organismes certificateurs',
    'header.othmQualifications': 'Qualifications OTHM',
    'header.qualifiQualifications': 'Qualifications QUALIFI',
    'header.toggleMenu': 'Basculer le menu',
    'footer.tagline': "Nous donnons les moyens aux apprenants du Royaume-Uni grâce à des programmes d'enseignement à distance flexibles et accrédités. Étudiez à votre rythme et atteignez vos objectifs professionnels.",
    'footer.courseCategories': 'Catégories de cours',
    'footer.quickLinks': 'Liens rapides',
    'footer.contactUs': 'Nous contacter',
    'footer.accreditation': 'Organismes certificateurs disponibles via Atlas Academy : OTHM et QUALIFI.',
    'footer.privacyPolicy': 'Politique de confidentialité',
    'footer.termsConditions': 'Conditions générales',
    'footer.cookiePolicy': 'Politique de cookies',
    'footer.termsOfUse': "Conditions d'utilisation",
    'footer.rightsReserved': '© {year} Atlas Academy Ltd. Tous droits réservés.',
    'course.backToCourses': 'Retour aux cours',
    'course.notFound': 'Cours introuvable.',
    'course.students': 'étudiants',
    'course.reviews': 'avis',
    'course.aboutTitle': 'À propos de ce cours',
    'course.aboutSupplement': "Cette qualification est entièrement dispensée en ligne via notre portail étudiant sécurisé. Vous aurez accès à tous les supports de cours, évaluations et retours de tuteurs dès le premier jour d'inscription. Il n'y a pas d'horaires fixes — vous étudiez simplement selon votre emploi du temps.",
    'course.studyTitle': 'Ce que vous étudierez',
    'course.detailsTitle': 'Détails du cours',
    'course.detail.level': 'Niveau',
    'course.detail.duration': 'Durée',
    'course.detail.studyMode': "Mode d'étude",
    'course.detail.studyModeValue': 'En ligne / Apprentissage à distance',
    'course.detail.awardingBody': 'Organisme certificateur',
    'course.detail.assessment': 'Évaluation',
    'course.detail.assessmentValue': 'Portfolio / Devoirs écrits',
    'course.detail.certificate': 'Certificat',
    'course.detail.certificateValue': 'Qualification britannique officielle',
    'course.paymentOptions': 'Options de paiement',
    'course.toComplete': 'pour terminer',
    'course.modulesCount': 'modules de cours',
    'course.accreditedBy': 'Accrédité par',
    'course.flexiblePayment': 'Plan de paiement flexible disponible',
    'course.enrolNow': "S'inscrire maintenant",
    'course.requestCallback': 'Demander un rappel',
    'course.paySecurely': 'Payer en toute sécurité',
    'course.paymentHelp': 'Paiement sécurisé par Stripe.',
    'course.paymentSuccess': "Paiement effectué avec succès. Votre équipe d'inscription vérifiera les prochaines étapes sous peu.",
    'course.paymentCancelled': 'Le paiement a été annulé. Vous pouvez reprendre le paiement quand vous le souhaitez.',
    'course.paymentError': 'Impossible de démarrer le paiement sécurisé pour le moment. Veuillez réessayer sous peu.',
    'course.enquirySubmitted': 'Demande envoyée !',
    'course.callbackSuccess': 'Notre équipe vous contactera dans les 24 heures pour finaliser votre inscription.',
    'course.callbackTitle': 'Demander un rappel',
    'course.firstName': 'Prénom',
    'course.lastName': 'Nom de famille',
    'course.emailAddress': 'Adresse e-mail',
    'course.phoneNumber': 'Numéro de téléphone',
    'course.sending': 'Envoi en cours…',
    'course.submitEnquiry': 'Envoyer la demande',
    'course.cancel': 'Annuler',
    'course.relatedCourses': 'Cours connexes',
    'course.youSave': 'Vous économisez £{amount}',
    'apply.heroEyebrow': 'Formulaire de candidature en ligne',
    'apply.heroTitle': 'Postulez pour votre cours',
    'apply.heroDescription': "Les informations demandées sur ce formulaire doivent être complétées avec le plus de détails possible afin de traiter votre candidature avec succès. Tous les champs marqués * doivent être remplis.",
    'apply.applicationReceived': 'Candidature reçue',
    'apply.successIntro': 'Merci, {firstName}. Votre candidature pour {courseTitle} a été soumise avec succès.',
    'apply.successFollowup': "Un membre de notre équipe d'admissions vous contactera dans un jour ouvrable pour vérifier vos coordonnées et finaliser votre inscription. Un e-mail de confirmation a été envoyé à {email}.",
    'apply.browseMoreCourses': "Parcourir d'autres cours",
    'apply.returnHome': "Retourner à l'accueil",
    'apply.courseSelection': 'Sélection du cours',
    'apply.selectCourse': 'Sélectionner un cours',
    'apply.loadingCourses': '-- Chargement des cours --',
    'apply.selectCoursePlaceholder': '-- Sélectionnez un cours --',
    'apply.currentCourseFee': 'Frais de cours actuels : £{amount}',
    'apply.studyMode': "Mode d'étude",
    'apply.studyMode.online': 'En ligne',
    'apply.studyMode.blended': 'Apprentissage mixte',
    'apply.feesMode': 'Frais de cours (Mode)',
    'apply.feesMode.selfFunded': 'Autofinancé',
    'apply.feesMode.company': "Parrainage d'entreprise",
    'apply.feesMode.loan': 'Prêt bancaire',
    'apply.paymentMethod': 'Méthode de paiement',
    'apply.paymentMethod.full': 'Paiement intégral (en une fois)',
    'apply.paymentMethod.installments': 'Mensualités',
    'apply.courseStartingDate': 'Date de début du cours',
    'apply.previouslyStudied': 'Avez-vous déjà étudié au Atlas Academy ?',
    'apply.heardAbout': 'Comment avez-vous entendu parler de nous pour la première fois ?',
    'apply.hear.google': 'Google',
    'apply.hear.bing': 'Bing',
    'apply.hear.social': 'Réseaux sociaux',
    'apply.hear.referral': 'Ami / Famille / Référence',
    'apply.hear.other': 'Autre (veuillez préciser)',
    'apply.selectOption': 'Sélectionner une option',
    'apply.other': 'Autre',
    'apply.yes': 'Oui',
    'apply.no': 'Non',
    'apply.pleaseSpecify': 'Veuillez préciser',
    'apply.promoCode': 'Code promo / Code de parrainage (optionnel)',
    'apply.personalInformation': 'Informations personnelles',
    'apply.title': 'Titre',
    'apply.title.mr': 'M.',
    'apply.title.mrs': 'Mme',
    'apply.title.miss': 'Mlle',
    'apply.title.ms': 'Mme',
    'apply.title.dr': 'Dr',
    'apply.firstName': 'Prénom',
    'apply.middleName': 'Deuxième prénom',
    'apply.surname': 'Nom de famille',
    'apply.dateOfBirth': 'Date de naissance',
    'apply.gender': 'Sexe',
    'apply.gender.male': 'Homme',
    'apply.gender.female': 'Femme',
    'apply.gender.other': 'Autre',
    'apply.mobileNumber': 'Numéro de mobile',
    'apply.email': 'E-mail',
    'apply.nationality': 'Nationalité',
    'apply.countryOfBirth': 'Pays de naissance',
    'apply.homeAddress': 'Adresse personnelle',
    'apply.addressLine1': "Ligne d'adresse 1",
    'apply.addressLine2': "Ligne d'adresse 2",
    'apply.city': 'Ville',
    'apply.stateRegion': 'État / Province / Région',
    'apply.postalCode': 'Code postal',
    'apply.country': 'Pays',
    'apply.countryPlaceholder': '-- Sélectionner un pays --',
    'apply.correspondenceAddress': 'Adresse de correspondance',
    'apply.sameAsHome': "Même que l'adresse personnelle",
    'apply.previousEducation': 'Détails de la formation précédente',
    'apply.qualification': 'Cours / Qualification',
    'apply.startDate': 'Date de début',
    'apply.endDate': 'Date de fin',
    'apply.workExperience': 'Expérience professionnelle (optionnel)',
    'apply.jobTitle': 'Intitulé du poste',
    'apply.employer': 'Employeur',
    'apply.responsibility': 'Responsabilités',
    'apply.supplyInfo': 'Informations de soutien pour la candidature',
    'apply.fullName': 'Nom complet',
    'apply.relationshipToYou': 'Relation avec vous',
    'apply.contactNumber': 'Numéro de contact',
    'apply.documents': 'Documents importants',
    'apply.documentsHelpPrefix': "Joignez vos pièces d'identité et documents justificatifs. Si vous ne pouvez pas les joindre ici, vous pouvez les envoyer par e-mail à",
    'apply.documentsHelpSuffix': 'après soumission.',
    'apply.idDocument': "Pièce d'identité (passeport / permis de conduire)",
    'apply.educationDocuments': 'Documents de formation',
    'apply.experienceDocuments': "Documents d'expérience",
    'apply.uploadPrompt': 'Cliquer pour télécharger',
    'apply.uploadFormats': 'PDF, JPG ou PNG',
    'apply.disclaimer': 'Avertissement',
    'apply.disclaimerText': "Il s'agit d'une page sécurisée et vos données sont cryptées afin qu'elles ne puissent pas être facilement lues par quiconque pourrait les intercepter entre votre ordinateur et ce site Web. Les données sont mises à la disposition des départements académiques et administratifs du Atlas Academy et sont utilisées uniquement pour le traitement des candidatures. Toutes les données seront traitées confidentiellement. Nous nous engageons à protéger vos droits et votre vie privée. Veuillez noter que le Atlas Academy n'est pas tenu de répondre aux formulaires de candidature qui ne sont pas entièrement remplis. Vérifiez attentivement vos informations avant de soumettre ce formulaire.",
    'apply.consentText': "Je confirme que les informations fournies sont vraies et exactes, et je consens au traitement de mes données personnelles aux fins de cette candidature.",
    'apply.submit': 'Soumettre la candidature',
    'apply.submitting': 'Soumission en cours…',
    'apply.errorConsent': "Veuillez accepter l'avertissement pour continuer.",
    'apply.errorSubmission': 'La soumission a échoué. Veuillez vérifier vos informations et réessayer.',
    // ── Home page ──────────────────────────────────────────────────────
    'home.hero.eyebrow': 'Qualifications de OTHM et QUALIFI',
    'home.hero.title1': 'Votre porte d\'entrée vers',
    'home.hero.titleHighlight': 'Flexible',
    'home.hero.title2': 'l\'apprentissage à distance',
    'home.hero.desc1': 'Étudiez plus de 100 cours accrédités en ligne conçus pour s\'adapter à votre vie. Pas d\'horaire fixe — apprenez quand et où vous le souhaitez.',
    'home.hero.desc2': 'Inscrivez-vous aujourd\'hui avec un faible acompte. Rien à payer avant le mois prochain.',
    'home.hero.searchPlaceholder': 'Rechercher un cours...',
    'home.hero.searchBtn': 'Rechercher',
    'home.feature.payment.title': 'Plans de paiement flexibles',
    'home.feature.payment.desc': 'Inscrivez-vous avec un petit acompte. Répartissez le coût restant en versements mensuels.',
    'home.feature.pace.title': 'Étudiez à votre rythme',
    'home.feature.pace.desc': 'Pas d\'échéances fixes ni d\'horaires de cours. Étudiez quand cela vous convient, avec jusqu\'à 12 mois pour terminer.',
    'home.feature.tutor.title': 'Soutien complet du tuteur',
    'home.feature.tutor.desc': 'Votre tuteur dédié est toujours disponible via notre portail en ligne 24h/24 pour guider votre progression.',
    'home.career.eyebrow': 'Orientation professionnelle gratuite',
    'home.career.title': 'Besoin d\'aide pour choisir votre prochaine étape ?',
    'home.career.desc': 'Que vous souhaitiez progresser dans votre rôle actuel, changer de carrière ou reprendre le travail après une pause, nos conseillers expérimentés sont là pour vous aider — entièrement gratuitement.',
    'home.career.point1': 'Recommandations de cours personnalisées',
    'home.career.point2': 'Conseils sur les parcours professionnels',
    'home.career.point3': 'Aide sur les options de financement et de paiement',
    'home.career.point4': 'Conseils sur les conditions d\'admission',
    'home.career.cta': 'Parler à un conseiller',
    'home.courses.eyebrow': 'Nos cours',
    'home.courses.title': 'Qualifications populaires',
    'home.courses.browseAll': 'Parcourir tous les cours',
    'home.courses.viewAll': 'Voir les 100+ cours',
    'home.testimonials.eyebrow': 'Témoignages d\'étudiants',
    'home.testimonials.title': 'Ce que disent nos apprenants',
    'home.awarding.label': 'Organismes certificateurs partenaires',
    'home.awarding.regulatedBody': 'Organisme certificateur agréé',
    'home.awarding.ukQA': 'Assurance qualité britannique',
    'home.cta.title': 'Prêt à commencer votre parcours d\'apprentissage ?',
    'home.cta.desc': 'Inscrivez-vous aujourd\'hui, payez un petit acompte et accédez immédiatement à vos supports de cours. Pas d\'attente, pas d\'inscription compliquée.',
    'home.cta.browseCourses': 'Parcourir les cours',
    'home.cta.speakAdviser': 'Parler à un conseiller',
    'shared.badge.sale': 'PROMO',
    // ── About page ─────────────────────────────────────────────────────
    'about.hero.eyebrow': 'À propos de nous',
    'about.hero.title': 'Qui sommes-nous',
    'about.hero.desc': 'Atlas Academy est un prestataire de formation à distance basé au Royaume-Uni, dédié à rendre les qualifications accréditées accessibles à tous, quels que soient leur parcours, leur lieu de résidence ou leur emploi du temps.',
    'about.mission.eyebrow': 'Notre mission',
    'about.mission.title': 'Supprimer les obstacles à une éducation de qualité',
    'about.mission.para1': "Nous croyons que l'endroit où vous vivez, ce que vous gagnez et la charge de votre vie ne devraient jamais vous empêcher d'obtenir une qualification professionnelle. C'est pourquoi nous avons construit chaque aspect de notre service autour de la flexibilité, de l'accessibilité financière et d'un soutien exceptionnel aux apprenants.",
    'about.mission.para2': "Depuis notre fondation, nous avons aidé des milliers d'adultes actifs, de parents et de personnes en reconversion à obtenir les accréditations nécessaires pour progresser. Nos cours sont entièrement en ligne, à rythme libre et soutenus à chaque étape.",
    'about.mission.point1': 'Plus de 5 000 apprenants diplômés avec succès',
    'about.mission.point2': '94 % de taux de réussite et d\'achèvement des cours',
    'about.mission.point3': 'Cours issus d\'organismes certificateurs tels que OTHM et QUALIFI',
    'about.mission.point4': 'Aucune présence en classe requise',
    'about.values.eyebrow': 'Nos valeurs',
    'about.values.title': 'Ce en quoi nous croyons',
    'about.values.accessibility.title': 'Accessibilité',
    'about.values.accessibility.desc': "L'éducation doit être accessible à tous. Nous supprimons les obstacles physiques, financiers et logistiques à l'apprentissage.",
    'about.values.quality.title': 'Qualité',
    'about.values.quality.desc': "Chaque qualification que nous proposons est réglementée et accréditée. Nous ne faisons jamais de compromis sur les normes académiques.",
    'about.values.learner.title': "Centré sur l'apprenant",
    'about.values.learner.desc': "Nos étudiants passent en premier. Chaque décision que nous prenons est guidée par ce qui est le mieux pour l'apprenant.",
    'about.values.community.title': 'Communauté',
    'about.values.community.desc': 'Nous construisons des relations durables avec nos étudiants et restons investis dans leur réussite à long terme.',
    'about.team.eyebrow': 'Notre équipe',
    'about.team.title': "Rencontrez l'équipe",
    'about.team.desc': 'Des éducateurs et professionnels expérimentés engagés pour votre réussite.',
    'about.accreditations.label': 'Organismes certificateurs avec lesquels nous travaillons',
    'about.cta.title': "Commencez à apprendre avec nous aujourd'hui",
    'about.cta.desc': 'Rejoignez des milliers d\'apprenants qui ont déjà franchi la prochaine étape de leur carrière.',
    'about.cta.btn': 'Explorer nos cours',
    // ── Contact page ───────────────────────────────────────────────────
    'contact.hero.eyebrow': 'Contactez-nous',
    'contact.hero.title': 'Nous contacter',
    'contact.hero.desc': 'Nos conseillers étudiants bienveillants sont là pour répondre à toutes vos questions sur les cours, l\'inscription ou les options de paiement.',
    'contact.info.title': 'Comment nous joindre',
    'contact.info.phone': 'Téléphone',
    'contact.info.email': 'E-mail',
    'contact.info.address': 'Adresse',
    'contact.info.hours': 'Heures de bureau',
    'contact.info.hoursValue': 'Lun–Ven : 9h00 – 17h30',
    'contact.info.portalHours': 'Portail en ligne : 24h/24',
    'contact.prefer.title': 'Vous préférez parler à quelqu\'un ?',
    'contact.prefer.desc': 'Nos conseillers sont disponibles du lundi au vendredi pour répondre à vos questions et vous aider à choisir le bon cours.',
    'contact.prefer.cta': 'Appelez-nous maintenant →',
    'contact.success.title': 'Message reçu !',
    'contact.success.desc': 'Merci de nous avoir contactés. L\'un de nos conseillers étudiants vous répondra dans un jour ouvrable. Un e-mail de confirmation vous a été envoyé.',
    'contact.success.another': 'Envoyer un autre message',
    'contact.form.title': 'Envoyez-nous un message',
    'contact.form.desc': 'Remplissez le formulaire ci-dessous et nous vous répondrons dès que possible.',
    'contact.form.firstName': 'Prénom',
    'contact.form.lastName': 'Nom de famille',
    'contact.form.emailAddress': 'Adresse e-mail',
    'contact.form.phoneNumber': 'Numéro de téléphone',
    'contact.form.subject': 'Sujet',
    'contact.form.message': 'Message',
    'contact.form.selectTopic': 'Sélectionnez un sujet…',
    'contact.form.topic.courseEnquiry': 'Renseignement sur un cours',
    'contact.form.topic.enrolment': 'Inscription et enregistrement',
    'contact.form.topic.fees': 'Frais et plans de paiement',
    'contact.form.topic.support': 'Soutien aux étudiants',
    'contact.form.topic.tech': 'Aide technique',
    'contact.form.topic.general': 'Renseignement général',
    'contact.form.sending': 'Envoi en cours…',
    'contact.form.send': 'Envoyer le message',
    'contact.form.error': 'Échec de l\'envoi du message. Veuillez réessayer ou nous appeler directement.',
    // ── How It Works page ──────────────────────────────────────────────
    'hiw.hero.eyebrow': 'Le processus',
    'hiw.hero.title': 'Comment ça marche',
    'hiw.hero.desc': 'Commencer est simple. Du choix de votre cours à la réception de votre certificat, nous vous guidons à chaque étape.',
    'hiw.step1.title': 'Choisissez votre cours',
    'hiw.step1.desc': 'Parcourez notre gamme complète de qualifications accréditées. Utilisez nos filtres pour affiner par domaine, niveau ou organisme certificateur. Vous ne savez pas par où commencer ? Nos conseillers vous orienteront dans la bonne direction.',
    'hiw.step1.tip': 'La plupart des apprenants commencent par une qualification de niveau 3 s\'ils ont une certaine expérience préalable dans leur domaine choisi.',
    'hiw.step2.title': "S'inscrire en ligne",
    'hiw.step2.desc': 'Remplissez notre formulaire d\'inscription en ligne simple. Choisissez votre option de paiement préférée — vous pouvez payer en totalité ou commencer avec un petit acompte et étaler le reste en versements mensuels.',
    'hiw.step2.tip': 'Votre acompte sécurise votre place immédiatement. Sans attente — vous accédez au portail le jour même.',
    'hiw.step3.title': 'Étudiez à votre rythme',
    'hiw.step3.desc': 'Connectez-vous à votre portail étudiant personnel et commencez à étudier immédiatement. Tous les supports pédagogiques, évaluations et ressources sont disponibles 24h/24. Votre tuteur dédié est là quand vous avez besoin de conseils.',
    'hiw.step3.tip': 'La plupart des étudiants consacrent environ 8 à 10 heures par semaine. Il n\'y a pas de sessions obligatoires ni d\'échéances.',
    'hiw.step4.title': 'Obtenez votre qualification',
    'hiw.step4.desc': 'Une fois que vous avez complété toutes les unités et évaluations, votre travail est vérifié par l\'organisme certificateur. Votre certificat officiel, nationalement reconnu, est émis et envoyé directement chez vous.',
    'hiw.step4.tip': 'Notre taux de réussite de 94 % reflète la qualité de notre soutien tutoral et de nos supports de cours.',
    'hiw.benefits.eyebrow': 'Les avantages',
    'hiw.benefits.title': 'Pourquoi choisir l\'apprentissage à distance ?',
    'hiw.benefit1.title': 'Étudiez selon votre vie',
    'hiw.benefit1.desc': 'Pas de déplacements, pas d\'horaires fixes. Apprenez pendant votre pause déjeuner, le soir ou le week-end — comme bon vous semble.',
    'hiw.benefit2.title': 'Gagnez pendant que vous apprenez',
    'hiw.benefit2.desc': 'Continuez dans votre rôle actuel tout en travaillant vers votre prochaine qualification. Beaucoup de nos étudiants trouvent que leurs études améliorent directement leur travail quotidien.',
    'hiw.benefit3.title': 'Économisez sur les coûts',
    'hiw.benefit3.desc': 'Pas d\'hébergement, pas de transport, pas de stationnement. L\'apprentissage à distance est nettement plus rentable que l\'étude en classe traditionnelle.',
    'hiw.benefit4.title': 'Résultats nationalement reconnus',
    'hiw.benefit4.desc': 'Nos qualifications ont le même poids que celles obtenues en classe. Les employeurs et les universités les reconnaissent également.',
    'hiw.benefit5.title': 'Apprenez à votre propre vitesse',
    'hiw.benefit5.desc': 'Prenez votre temps sur les sujets difficiles et avancez rapidement dans les domaines que vous trouvez simples. Le cours s\'adapte à vous, pas l\'inverse.',
    'hiw.benefit6.title': 'Soutien tutoral permanent',
    'hiw.benefit6.desc': 'Vous n\'étudiez jamais vraiment seul. Votre tuteur est accessible via notre portail à tout moment, fournissant des conseils et des retours personnalisés.',
    'hiw.faqs.eyebrow': 'Questions et réponses',
    'hiw.faqs.title': 'Questions fréquemment posées',
    'hiw.cta.title': 'Prêt à commencer ?',
    'hiw.cta.desc': 'Parcourez nos cours et inscrivez-vous aujourd\'hui avec un faible acompte.',
    'hiw.cta.viewCourses': 'Voir les cours',
    'hiw.cta.contact': 'Nous contacter',
    // ── Pricing page ───────────────────────────────────────────────────
    'pricing.hero.eyebrow': 'Tarifs transparents',
    'pricing.hero.title': 'Frais et tarifs des cours',
    'pricing.hero.desc': 'Tous les cours sont tarifiés individuellement selon le niveau et la qualification. Des plans de paiement flexibles sont disponibles pour chaque cours, sans frais cachés.',
    'pricing.cards.title': 'Tarifs des cours par niveau',
    'pricing.cards.desc': 'Les prix varient selon les cours. Les fourchettes ci-dessous sont indicatives.',
    'pricing.cards.popular': 'Le plus populaire',
    'pricing.cards.priceNote': 'Les prix varient selon les cours de ce niveau',
    'pricing.payment.eyebrow': 'Options de paiement',
    'pricing.payment.title': 'Des façons flexibles de payer',
    'pricing.payment.full.title': 'Paiement intégral',
    'pricing.payment.full.desc': 'Payez la totalité des frais du cours d\'avance. C\'est l\'option la plus simple et garantit qu\'aucun paiement ultérieur n\'est dû.',
    'pricing.payment.deposit.title': 'Plan à faible acompte',
    'pricing.payment.deposit.desc': 'Réservez votre place avec un petit acompte — souvent aussi peu que £50. Vous ne serez pas prélevé avant le mois suivant.',
    'pricing.payment.installments.title': 'Mensualités',
    'pricing.payment.installments.desc': 'Répartissez le solde restant sur 2 à 6 mois selon le cours. Nous mettrons en place un simple accord de prélèvement automatique.',
    'pricing.discuss.title': 'Besoin de discuter des frais ?',
    'pricing.discuss.desc': 'Nos conseillers sont heureux de vous aider à trouver l\'arrangement de paiement le plus approprié. Pas de pression et pas d\'obligation — juste des conseils francs et honnêtes.',
    'pricing.discuss.call': 'Appel : +44 (0) 20 3355 5646',
    'pricing.discuss.enquiry': 'Envoyer une demande',
    'pricing.faqTitle': 'Questions liées aux frais',
    'pricing.faq1.q': 'Y a-t-il des frais cachés ?',
    'pricing.faq1.a': 'Non. Le prix que vous voyez pour chaque cours est le coût total. Aucun frais d\'inscription, de matériels ou coûts surprises n\'est ajouté lors du paiement.',
    'pricing.faq2.q': 'Existe-t-il une politique de remboursement ?',
    'pricing.faq2.a': 'Oui. Vous avez droit à une période de réflexion après l\'inscription. Veuillez contacter notre équipe dans les 14 jours si vous souhaitez annuler. Les conditions générales s\'appliquent.',
    'pricing.faq3.q': 'Puis-je changer de cours après l\'inscription ?',
    'pricing.faq3.a': 'Dans la plupart des cas, oui. Parlez à votre conseiller étudiant et nous ferons de notre mieux pour vous transférer vers un programme plus adapté.',
    'pricing.faq4.q': 'Proposez-vous des réductions ?',
    'pricing.faq4.a': 'Nous effectuons des promotions saisonnières tout au long de l\'année. Des réductions groupées sont également disponibles lors de l\'inscription à plusieurs cours simultanément.',
    'pricing.cta.title': 'Prêt à vous inscrire ?',
    'pricing.cta.desc': 'Choisissez votre cours et commencez avec un petit acompte aujourd\'hui. Votre avenir commence maintenant.',
    'pricing.cta.btn': 'Parcourir tous les cours',
    'plan.l2.name': 'Cours de niveau 2',
    'plan.l2.desc': 'Qualifications d\'introduction pour ceux qui sont nouveaux dans un secteur ou qui reprennent leurs études.',
    'plan.l3.name': 'Cours de niveau 3',
    'plan.l3.desc': 'Qualifications qui font avancer la carrière des praticiens et des professionnels ambitieux.',
    'plan.l47.name': 'Cours niveaux 4–7',
    'plan.l47.desc': 'Qualifications de haut niveau pour les rôles seniors, la gestion et la pratique spécialisée.',
    'plan.feature.materials': 'Supports de cours complets en ligne',
    'plan.feature.tutorSupport': 'Soutien tutoral dédié',
    'plan.feature.priorityTutor': 'Soutien tutoral prioritaire',
    'plan.feature.seniorTutor': 'Soutien tutoral senior',
    'plan.feature.6months': "Jusqu'à 6 mois pour terminer",
    'plan.feature.12months': "Jusqu'à 12 mois pour terminer",
    'plan.feature.18months': "Jusqu'à 18 mois pour terminer",
    'plan.feature.digitalCert': "Certificat numérique à l'achèvement",
    'plan.feature.ofqualCert': 'Certificat officiel de l\'organisme certificateur',
    'plan.feature.payment': 'Plan de paiement flexible disponible',
    'plan.feature.portal': 'Accès au portail étudiant',
    'plan.feature.career': "Orientation professionnelle incluse",
    'plan.feature.progression': 'Orientation professionnelle et de progression',
    'plan.feature.priority': "Soutien à l'inscription prioritaire",
    // ── Student Support page ───────────────────────────────────────────
    'support.hero.eyebrow': 'Nous sommes là pour vous',
    'support.hero.title': 'Soutien aux étudiants',
    'support.hero.desc': "Étudier depuis chez vous ne signifie pas étudier seul. Nous offrons un soutien complet à chaque étape de votre parcours d'apprentissage.",
    'support.stats.portal': 'Accès au portail',
    'support.stats.response': 'Temps de réponse du tuteur',
    'support.stats.tutor': 'Soutien tutoral dédié',
    'support.resources.eyebrow': 'Services de soutien',
    'support.resources.title': 'Quel soutien offrons-nous ?',
    'support.tutor.title': 'Soutien du tuteur',
    'support.tutor.desc': "Chaque étudiant est associé à un tuteur spécialiste dédié dès le jour de son inscription. Obtenez des retours sur vos devoirs, des conseils sur les sujets difficiles et des encouragements tout au long de vos études.",
    'support.tutor.cta': 'En savoir plus',
    'support.materials.title': "Supports d'étude",
    'support.materials.desc': "Tout le contenu du cours est disponible via notre portail en ligne sécurisé, y compris les documents de lecture, les évaluations pratiques et les ressources multimédias — accessibles sur n'importe quel appareil.",
    'support.materials.cta': 'Explorer les cours',
    'support.assignments.title': 'Aide aux devoirs',
    'support.assignments.desc': "Vous ne savez pas comment aborder un devoir ? Nous fournissons des modèles de réponses, des documents d'orientation et des sessions individuelles avec le tuteur pour vous aider à soumettre votre meilleur travail.",
    'support.assignments.cta': "Obtenir de l'aide",
    'support.adviser.title': 'Chat avec le conseiller',
    'support.adviser.desc': "Nos conseillers étudiants sont disponibles par téléphone et e-mail du lundi au vendredi. Que vous ayez une question d'inscription ou que vous ayez besoin d'un soutien général, nous sommes là pour vous aider.",
    'support.adviser.cta': 'Nous contacter',
    'support.wellbeing.eyebrow': 'Bien-être des étudiants',
    'support.wellbeing.title': 'Votre bien-être nous importe',
    'support.wellbeing.desc': "Nous comprenons que les apprenants adultes jonglent souvent entre les études et les exigences du travail et de la vie familiale. Notre équipe de soutien aux étudiants est formée pour reconnaître quand quelqu'un peut avoir besoin d'un peu d'aide supplémentaire, et nous prenons contact de manière proactive pour s'assurer que personne ne soit laissé de côté.",
    'support.wellbeing.point1': 'Suivi personnel du bien-être par votre conseiller',
    'support.wellbeing.point2': 'Demandes de prolongation traitées avec compréhension',
    'support.wellbeing.point3': 'Pas de jugement — juste un soutien pratique',
    'support.wellbeing.point4': 'Orientation vers des ressources supplémentaires si nécessaire',
    'support.portal.eyebrow': 'Portail étudiant',
    'support.portal.title': 'Tout en un seul endroit',
    'support.portal.desc': "Notre portail d'apprentissage en ligne vous donne un accès instantané à tous vos supports de cours, soumissions de devoirs, messages du tuteur et suivi de progression. Disponible sur ordinateur, tablette et mobile.",
    'support.portal.feature1': 'Supports de cours',
    'support.portal.feature2': 'Messagerie tuteur',
    'support.portal.feature3': 'Accès 24h/24',
    'support.portal.feature4': 'Suivi de progression',
    'support.portal.enrolled': 'Déjà inscrit ?',
    'support.portal.enrolledDesc': 'Connectez-vous à votre portail étudiant pour accéder à vos supports de cours et vous connecter avec votre tuteur.',
    'support.portal.accessBtn': 'Accéder au portail étudiant',
    'support.portal.notEnrolled': 'Pas encore inscrit ?',
    'support.portal.browse': 'Parcourir les cours',
    'support.faqs.eyebrow': "Centre d'aide",
    'support.faqs.title': 'Questions de soutien courantes',
    'support.faqs.viewAll': 'Voir toutes les FAQ',
    'support.cta.title': "Vous avez une question à laquelle nous n'avons pas répondu ?",
    'support.cta.desc': "Nos conseillers sont prêts à vous aider. Contactez-nous et nous répondrons rapidement.",
    'support.cta.btn': 'Contacter le soutien aux étudiants',
    // ── Awarding Body page ─────────────────────────────────────────────
    'awarding.chip': 'Organisme certificateur partenaire',
    'awarding.chipLabel': 'Organisme certificateur',
    'awarding.avgRating': 'Note moyenne 4,8',
    'awarding.glance.title': 'En bref',
    'awarding.glance.body': 'Organisme certificateur',
    'awarding.glance.regulator': 'Régulateur',
    'awarding.glance.framework': 'Cadre',
    'awarding.glance.levels': 'Niveaux proposés',
    'awarding.glance.delivery': 'Mode de livraison',
    'awarding.glance.deliveryValue': 'En ligne / Mixte',
    'awarding.qualifications.eyebrow': 'Qualifications',
    'awarding.qualifications.viewAll': 'Voir tout',
    'awarding.other.eyebrow': 'Aussi disponible',
    'awarding.other.desc': "Nous délivrons le portefeuille complet des deux principaux organismes certificateurs vocatifs du Royaume-Uni.",
    'awarding.trust': 'Atlas Academy délivre des qualifications provenant d\'organismes certificateurs britanniques reconnus, notamment OTHM et QUALIFI. Votre certificat est émis par l\'organisme certificateur concerné et porte une valeur de crédit britannique reconnue.',
    // ── Courses page ───────────────────────────────────────────────────
    'courses.hero.eyebrow': 'Explorer nos qualifications',
    'courses.hero.title': 'Cours et qualifications',
    'courses.hero.desc': 'Parcourez plus de 100 cours d\'apprentissage à distance accrédités dans de nombreux secteurs. Toutes les études sont entièrement en ligne, flexibles et soutenues par un tuteur.',
    'courses.search.placeholder': 'Rechercher des cours...',
    'courses.filters.btn': 'Filtres',
    'courses.sort.featured': 'En vedette d\'abord',
    'courses.sort.priceLow': 'Prix : croissant',
    'courses.sort.priceHigh': 'Prix : décroissant',
    'courses.sort.rating': 'Mieux notés',
    'courses.filter.category': 'Catégorie',
    'courses.filter.level': 'Niveau',
    'courses.filter.body': 'Organisme certificateur',
    'courses.filter.allLevels': 'Tous les niveaux',
    'courses.filter.allBodies': 'Tous les organismes',
    'courses.filter.clear': 'Effacer tous les filtres',
    'courses.loading': 'Chargement des cours…',
    'courses.notFound': 'Aucun cours trouvé correspondant à vos critères.',
    'courses.clearFilters': 'Effacer les filtres',
    // ── STATS ──────────────────────────────────────────────────────────
    'stats.courses': 'Cours accrédités',
    'stats.levels': 'Niveaux de qualification',
    'stats.portal': 'Accès au portail en ligne',
    'stats.online': 'Étude en ligne et flexible',
    'shared.instalments.short': 'ou {count} × {amount} £',
    // ── FAQs ───────────────────────────────────────────────────────────
    'faq.1.q': "Comment fonctionne l'apprentissage à distance ?",
    'faq.1.a': "Tous les supports d'étude sont délivrés via notre portail d'apprentissage en ligne sécurisé. Une fois inscrit, vous aurez un accès immédiat au contenu de votre cours, y compris les documents de lecture, les devoirs et les retours du tuteur. Vous étudiez entièrement à votre propre rythme, sans horaire fixe.",
    'faq.2.q': 'Les qualifications sont-elles officiellement reconnues ?',
    'faq.2.a': 'Oui. Nos qualifications sont délivrées par des organismes certificateurs britanniques reconnus, notamment OTHM et QUALIFI. Elles sont largement valorisées par les employeurs et les organismes de progression au Royaume-Uni et à l\'international.',
    'faq.3.q': "Combien de temps ai-je pour terminer mon cours ?",
    'faq.3.a': 'La plupart des cours permettent jusqu\'à 12 mois pour terminer. Les programmes plus courts permettent généralement 6 mois. Si vous avez besoin de temps supplémentaire, vous pouvez parler à votre conseiller d\'une prolongation.',
    'faq.4.q': 'Quel soutien tutoral est disponible ?',
    'faq.4.a': 'Chaque étudiant se voit attribuer un tuteur spécialiste dédié qui peut être contacté via notre portail en ligne, par e-mail ou téléphone. Les tuteurs fournissent des retours sur les devoirs, répondent aux questions et offrent des encouragements tout au long de vos études.',
    'faq.5.q': "Puis-je m'inscrire si je n'ai pas de qualifications préalables ?",
    'faq.5.a': "Beaucoup de nos cours de niveau 2 et niveau 3 ne nécessitent pas de qualifications préalables. Les conditions d'admission varient selon le programme. Nos conseillers évalueront votre aptitude et recommanderont le point de départ le plus approprié.",
    'faq.6.q': 'Quelles options de paiement sont disponibles ?',
    'faq.6.a': "Vous pouvez payer la totalité des frais du cours d'avance ou étaler le coût avec l'un de nos plans de paiement flexibles. La plupart des cours ne nécessitent qu'un petit acompte pour commencer, le solde restant étant réparti en versements mensuels.",
    'faq.7.q': "Ai-je besoin d'équipement spécial ?",
    'faq.7.a': "Tout ce dont vous avez besoin est une connexion internet fiable et un appareil tel qu'un ordinateur portable, un ordinateur de bureau ou une tablette. Aucun logiciel spécialisé n'est requis. Notre portail est entièrement accessible depuis n'importe quel navigateur web moderne.",
    'faq.8.q': 'Comment serai-je évalué ?',
    'faq.8.a': "Les méthodes d'évaluation varient selon la qualification mais comprennent généralement des devoirs écrits, des études de cas et des travaux de portfolio. Il n'y a pas d'examens chronométrés pour la plupart des programmes. Tous les travaux sont soumis numériquement via le portail étudiant.",
    'faq.9.q': "Puis-je étudier tout en travaillant à temps plein ?",
    'faq.9.a': "Absolument. Nos cours sont spécifiquement conçus pour les adultes actifs. Il n'y a pas d'horaires de cours fixes et tous les matériaux sont disponibles 24h/24. La plupart des étudiants étudient 8 à 10 heures par semaine, mais c'est entièrement à vous de décider.",
    'faq.10.q': "Que se passe-t-il après avoir obtenu ma qualification ?",
    'faq.10.a': "Après réussite, vous recevrez votre certificat officiel de l'organisme certificateur. Nous fournissons également un service d'orientation professionnelle et pouvons vous conseiller sur les voies d'études supplémentaires ou de progression dans votre secteur.",
  },
};

const SiteLanguageContext = createContext(null);

export function SiteLanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') {
      return 'en';
    }

    const savedLanguage = window.localStorage.getItem(STORAGE_KEY);
    return LANGUAGE_OPTIONS.some(option => option.code === savedLanguage) ? savedLanguage : 'en';
  });

  useEffect(() => {
    const current = LANGUAGE_OPTIONS.find(option => option.code === language) || LANGUAGE_OPTIONS[0];

    document.documentElement.lang = language;
    document.documentElement.dir = current.dir;
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const t = useCallback((key, fallback = '') => {
    return TRANSLATIONS[language]?.[key] || TRANSLATIONS.en[key] || fallback || key;
  }, [language]);

  const value = useMemo(() => {
    const current = LANGUAGE_OPTIONS.find(option => option.code === language) || LANGUAGE_OPTIONS[0];
    return {
      direction: current.dir,
      language,
      languages: LANGUAGE_OPTIONS,
      setLanguage,
      t,
    };
  }, [language, t]);

  return <SiteLanguageContext.Provider value={value}>{children}</SiteLanguageContext.Provider>;
}

export function useSiteLanguage() {
  const context = useContext(SiteLanguageContext);

  if (!context) {
    throw new Error('useSiteLanguage must be used within SiteLanguageProvider');
  }

  return context;
}